import { useState, useEffect, useRef } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ReorderableTable, type ReorderableColumn } from "@/components/ReorderableTable";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Package, Eye, Scissors, CheckSquare, ClipboardCheck, Send,
  FileText, Printer, Tag, BarChart2, X, Check, AlertTriangle, PackageSearch, Flag, MessageSquare, Paperclip, Loader2, Bell
} from "lucide-react";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import QRCode from "qrcode";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { EtiquetasChips, EtiquetasEditor } from "@/components/Etiquetas";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

// ─── Tipos ────────────────────────────────────────────────────────────────────

type StatusPedido =
  | "pendente" | "em separação" | "para conferencia" | "em conferencia"
  | "em revisão" | "solicitar expedição" | "aguardando expedição"
  | "finalizado" | "cancelado" | "devolução";

const statusConfig: Record<StatusPedido, { bg: string; text: string }> = {
  "pendente":             { bg: "bg-yellow-100", text: "text-yellow-800" },
  "em separação":         { bg: "bg-blue-100",   text: "text-blue-800" },
  "para conferencia":     { bg: "bg-indigo-100", text: "text-indigo-800" },
  "em conferencia":       { bg: "bg-purple-100", text: "text-purple-800" },
  "em revisão":           { bg: "bg-orange-100", text: "text-orange-800" },
  "solicitar expedição":  { bg: "bg-cyan-100",   text: "text-cyan-800" },
  "aguardando expedição": { bg: "bg-teal-100",   text: "text-teal-800" },
  "finalizado":           { bg: "bg-green-100",  text: "text-green-800" },
  "cancelado":            { bg: "bg-red-100",    text: "text-red-800" },
  "devolução":            { bg: "bg-rose-100",   text: "text-rose-800" },
};

function StatusBadge({ status }: { status: string }) {
  const config = statusConfig[status as StatusPedido] ?? { bg: "bg-gray-100", text: "text-gray-800" };
  return (
    <span className={`px-2 py-1 rounded text-xs font-medium ${config.bg} ${config.text}`}>
      {status}
    </span>
  );
}

function AvisoStatusPedido({ status }: { status: string }) {
  let aviso: string;
  switch (status) {
    case "aguardando finalização":
      aviso = "Pedido já coletado";
      break;
    case "aguardando expedição":
    case "solicitar expedição":
      aviso = "Aguardando coleta";
      break;
    case "em separação":
    case "para conferencia":
    case "em conferencia":
      aviso = "Pedido em preparo";
      break;
    default:
      return null;
  }
  return <span className="ml-2 text-xs">{aviso}</span>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function calcularDiasEmAberto(dataPedido: Date) {
  const hoje = new Date();
  const data = new Date(dataPedido);
  const diffTime = Math.abs(hoje.getTime() - data.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

function formatDate(d: any) {
  if (!d) return "-";
  return new Date(d.toString().includes("T") ? d : d + "T12:00:00").toLocaleDateString("pt-BR");
}

function adicionarTempoUtil(inicio: number, duracao: number) {
  const inicioExpediente = 8;
  const fimExpediente = 17;
  let cursor = new Date(inicio);
  let restante = duracao;
  while (restante > 0) {
    const inicioDia = new Date(cursor);
    inicioDia.setHours(inicioExpediente, 0, 0, 0);
    const fimDia = new Date(cursor);
    fimDia.setHours(fimExpediente, 0, 0, 0);
    if (cursor.getDay() === 0 || cursor.getDay() === 6 || cursor.getTime() >= fimDia.getTime()) {
      cursor.setDate(cursor.getDate() + 1);
      cursor.setHours(inicioExpediente, 0, 0, 0);
      continue;
    }
    if (cursor.getTime() < inicioDia.getTime()) cursor = inicioDia;
    const trecho = Math.min(restante, fimDia.getTime() - cursor.getTime());
    cursor = new Date(cursor.getTime() + trecho);
    restante -= trecho;
  }
  return cursor.getTime();
}

function diferencaTempoUtil(inicio: number, fim: number) {
  const inicioExpediente = 8;
  const fimExpediente = 17;
  const sinal = fim >= inicio ? 1 : -1;
  let cursor = new Date(Math.min(inicio, fim));
  const limite = Math.max(inicio, fim);
  let total = 0;
  while (cursor.getTime() < limite) {
    const inicioDia = new Date(cursor);
    inicioDia.setHours(inicioExpediente, 0, 0, 0);
    const fimDia = new Date(cursor);
    fimDia.setHours(fimExpediente, 0, 0, 0);
    const proximoDia = new Date(cursor);
    proximoDia.setHours(24, 0, 0, 0);
    const fimTrecho = Math.min(proximoDia.getTime(), limite);
    if (cursor.getDay() !== 0 && cursor.getDay() !== 6) {
      const inicioTrecho = Math.max(cursor.getTime(), inicioDia.getTime());
      const fimUtil = Math.min(fimTrecho, fimDia.getTime());
      if (fimUtil > inicioTrecho) total += fimUtil - inicioTrecho;
    }
    cursor = new Date(fimTrecho);
  }
  return total * sinal;
}

function calcularPrazoPedido(pedido: any, agora: number) {
  if (!pedido.dataPedido || !pedido.prazoPreparo) return null;
  const [horas, minutos, segundos] = String(pedido.prazoPreparo).split(":").map(Number);
  if ([horas, minutos, segundos].some(Number.isNaN)) return null;
  const paraData = (valor: any) => new Date(String(valor).includes("T") ? valor : String(valor).replace(" ", "T")).getTime();
  const limite = adicionarTempoUtil(paraData(pedido.dataPedido), (horas * 3600 + minutos * 60 + segundos) * 1000);
  const finalizadoEm = pedido.dataAutorizacaoNfe ? paraData(pedido.dataAutorizacaoNfe) : null;
  const diferenca = diferencaTempoUtil(finalizadoEm ?? agora, limite);
  const totalMinutos = Math.floor(Math.abs(diferenca) / 60000);
  const restante = `${diferenca < 0 ? "-" : ""}${String(Math.floor(totalMinutos / 60)).padStart(2, "0")}:${String(totalMinutos % 60).padStart(2, "0")}`;
  return {
    restante,
    status: finalizadoEm ? (diferenca >= 0 ? "Finalizado no prazo" : "Finalizado em atraso") : (diferenca >= 0 ? "No prazo" : "Em atraso"),
  };
}

const SEM_VENDEDOR_ID = "__sem_vendedor__";

type ItemSeparacaoMassa = {
  productCod: string;
  productDescription: string;
  quantidade: number;
  enderecos: string[];
  pedidos: Array<{
    id: number;
    numero: string;
    quantidade: number;
  }>;
};

function consolidarItensSeparacaoMassa(pedidos: any[]): ItemSeparacaoMassa[] {
  const porCodigo = new Map<string, ItemSeparacaoMassa>();

  for (const pedido of pedidos) {
    for (const item of pedido.itens || []) {
      const productCod = String(item.productCod || "-");
      const existente = porCodigo.get(productCod);
      const enderecos = (item.enderecos || []).filter(Boolean);
      const quantidade = Number(item.quantidade || 0);
      const pedidoId = Number(pedido.id);
      const numeroPedido = String(pedido.id);

      if (existente) {
        existente.quantidade += quantidade;
        existente.enderecos = Array.from(new Set([...existente.enderecos, ...enderecos]));
        const pedidoExistente = existente.pedidos.find((pedidoItem) => pedidoItem.id === pedidoId);
        if (pedidoExistente) {
          pedidoExistente.quantidade += quantidade;
        } else {
          existente.pedidos.push({ id: pedidoId, numero: numeroPedido, quantidade });
        }
      } else {
        porCodigo.set(productCod, {
          productCod,
          productDescription: item.productDescription || "-",
          quantidade,
          enderecos: Array.from(new Set(enderecos)),
          pedidos: [{ id: pedidoId, numero: numeroPedido, quantidade }],
        });
      }
    }
  }

  return Array.from(porCodigo.values())
    .map((item) => ({ ...item, pedidos: item.pedidos.sort((a, b) => a.numero.localeCompare(b.numero, "pt-BR", { numeric: true })) }))
    .sort((a, b) => a.productCod.localeCompare(b.productCod, "pt-BR", { numeric: true }));
}

function formatQuantidadeSeparacaoMassa(quantidade: number) {
  return quantidade.toLocaleString("pt-BR", { maximumFractionDigits: 4 });
}

function gerarPdfSeparacaoMassa(itens: ItemSeparacaoMassa[], pedidos: any[], win: Window) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const largura = 297;
  const margem = 12;
  const larguraTabela = largura - margem * 2;
  const colunas = { codigo: 14, descricao: 48, quantidade: 180, endereco: 210 };
  const numerosPedidos = pedidos
    .map((pedido) => String(pedido.id))
    .sort((a, b) => a.localeCompare(b, "pt-BR", { numeric: true }));
  const rodapeLinhas = doc.splitTextToSize(`Pedidos selecionados: ${numerosPedidos.map((numero) => `#${numero}`).join(", ")}`, larguraTabela);
  const limiteY = 210 - margem - rodapeLinhas.length * 3.2 - 5;
  let y = 20;

  const cabecalho = () => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Separação em Massa", margem, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(`${pedidos.length} pedido(s) selecionado(s) · ${new Date().toLocaleDateString("pt-BR")}`, largura - margem, y, { align: "right" });
    y += 8;
    doc.setFillColor(240, 240, 240);
    doc.rect(margem, y - 5, largura - margem * 2, 7, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("Código", colunas.codigo, y);
    doc.text("Descrição", colunas.descricao, y);
    doc.text("Quantidade", colunas.quantidade, y);
    doc.text("Endereço", colunas.endereco, y);
    y += 6;
  };

  const rodape = () => {
    doc.setDrawColor(190, 190, 190);
    doc.line(margem, limiteY + 3, largura - margem, limiteY + 3);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(90, 90, 90);
    doc.text(rodapeLinhas, margem, limiteY + 7);
    doc.setTextColor(0, 0, 0);
  };

  cabecalho();

  for (const item of itens) {
    const descricao = doc.splitTextToSize(item.productDescription, 122);
    const endereco = doc.splitTextToSize(item.enderecos.join(", ") || "-", 72);
    const alturaLinha = Math.max(descricao.length, endereco.length, 1) * 4.5 + 5;
    const alturaDetalhes = 7 + item.pedidos.length * 4.5;
    const alturaBloco = alturaLinha + alturaDetalhes;

    if (y + alturaBloco > limiteY) {
      rodape();
      doc.addPage();
      y = 20;
      cabecalho();
    }

    const inicioBloco = y - 4;
    doc.setDrawColor(190, 190, 190);
    doc.setLineWidth(0.2);
    doc.rect(margem, inicioBloco, larguraTabela, alturaBloco);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(item.productCod, colunas.codigo, y);
    doc.text(descricao, colunas.descricao, y);
    doc.text(formatQuantidadeSeparacaoMassa(item.quantidade), colunas.quantidade, y);
    doc.text(endereco, colunas.endereco, y);
    y += alturaLinha;

    doc.setFillColor(247, 247, 247);
    doc.rect(margem + 0.2, y - 4, larguraTabela - 0.4, alturaDetalhes, "F");
    doc.setDrawColor(215, 215, 215);
    doc.line(margem, y - 4, largura - margem, y - 4);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("DETALHAMENTO POR PEDIDO", margem + 4, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    item.pedidos.forEach((pedidoItem, indice) => {
      const linhaY = y + 4.5 + indice * 4.5;
      doc.text(`• Pedido #${pedidoItem.numero}`, margem + 8, linhaY);
      doc.text(`${formatQuantidadeSeparacaoMassa(pedidoItem.quantidade)} un`, margem + 64, linhaY);
    });
    y = inicioBloco + alturaBloco + 5;
  }

  rodape();

  const pdfUrl = URL.createObjectURL(doc.output("blob"));
  win.location.href = pdfUrl;
  window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 60_000);
}

function gerarExcelSeparacaoMassa(itens: ItemSeparacaoMassa[]) {
  const planilha = XLSX.utils.json_to_sheet(itens.map((item) => ({
    "Código": item.productCod,
    "Descrição": item.productDescription,
    "Quantidade": item.quantidade,
    "Endereço": item.enderecos.join(", ") || "-",
    "Detalhamento por Pedido": item.pedidos.map((pedidoItem) => `Pedido #${pedidoItem.numero}: ${formatQuantidadeSeparacaoMassa(pedidoItem.quantidade)} un`).join("\n"),
  })));
  planilha["!cols"] = [{ wch: 18 }, { wch: 55 }, { wch: 14 }, { wch: 40 }, { wch: 38 }];
  planilha["!rows"] = itens.map((item) => ({ hpt: Math.max(18, item.pedidos.length * 15) }));
  const arquivo = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(arquivo, planilha, "Separação em Massa");
  XLSX.writeFile(arquivo, `separacao_em_massa_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

// ─── PDF Romaneio de Separação ─────────────────────────────────────────────────

function gerarPaginaSeparacao(pedido: any) {
  const rows = (pedido.itens || []).map((item: any) => `
    <tr>
      <td style="border:1px solid #ccc;padding:6px">${item.productCod || "-"}</td>
      <td style="border:1px solid #ccc;padding:6px">${item.productDescription || "-"}</td>
      <td style="border:1px solid #ccc;padding:6px;text-align:center">${parseFloat(item.quantidade).toFixed(0)}</td>
      <td style="border:1px solid #ccc;padding:6px;text-align:center">${item.qtdCaixaMaster || "-"}</td>
      <td style="border:1px solid #ccc;padding:6px">${(item.enderecos || []).join(", ") || "-"}</td>
    </tr>
  `).join("");
  const totalLinhas = (pedido.itens || []).length;

  return `
    <section class="romaneio">
      <h2>Romaneio de Separação — Pedido #${pedido.id}</h2>
      <div class="info">
        <strong>Emitente:</strong> ${pedido.emitenteFantasia || pedido.emitenteNome || "-"}<br>
        <strong>Destinatário:</strong> ${pedido.destinatarioFantasia || pedido.destinatarioRazao || "-"}<br>
        <strong>Data do Pedido:</strong> ${formatDate(pedido.criadoEm)}<br>
        <strong>Tipo de Orçamento:</strong> ${pedido.tipoOrcamentoNome || "-"}<br>
        <strong>Tipo de Frete:</strong> ${pedido.tipoFrete || "-"} / ${pedido.tipoEntrega || "-"}<br>
        ${pedido.obsPedidoInterno ? `<strong>Obs. Interno:</strong> ${pedido.obsPedidoInterno}<br>` : ""}
        ${pedido.obsPedidoDestinatario ? `<strong>Obs. Destinatário:</strong> ${pedido.obsPedidoDestinatario}<br>` : ""}
      </div>
      <table>
        <thead>
          <tr>
            <th>Código</th><th>Descrição</th><th>Qtd.</th><th>Qtd. Cx. Master</th><th>Endereços</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
        <tfoot>
          <tr>
            <td colspan="5" class="rodape">Pedido #${pedido.id} · Cliente: ${pedido.destinatarioFantasia || pedido.destinatarioRazao || "-"} · Emitente: ${pedido.emitenteFantasia || pedido.emitenteNome || "-"} · Total de itens: ${totalLinhas}</td>
          </tr>
        </tfoot>
      </table>
    </section>
  `;
}

function gerarPDFSeparacaoLote(pedidos: any[], win: Window) {
  const html = `
    <!DOCTYPE html><html><head>
    <meta charset="utf-8">
    <title>Romaneios de Separação</title>
    <style>
      body { font-family: Arial, sans-serif; font-size: 12px; margin: 20px; }
      h2 { margin-bottom: 4px; }
      .info { margin-bottom: 12px; line-height: 1.6; }
      .romaneio { page-break-after: always; }
      .romaneio:last-of-type { page-break-after: auto; }
      table { border-collapse: collapse; width: 100%; margin-top: 12px; }
      th { background: #f0f0f0; border: 1px solid #ccc; padding: 6px; text-align: left; }
      tfoot { display: table-footer-group; }
      .rodape { border-top: 1px solid #ccc; padding: 6px; text-align: right; font-size: 10px; color: #555; }
      @media print { button { display: none; } }
    </style>
    </head><body>
    ${pedidos.map(gerarPaginaSeparacao).join("")}
    <br>
    <button onclick="window.print()">Imprimir</button>
    </body></html>
  `;

  win.document.write(html);
  win.document.close();
}

// ─── PDF Etiquetas de Volume Misto (10×15 cm, QR único por volume) ─────────────

async function gerarEtiquetasVolumeMisto(pedido: any, totalVolumes: number, janela?: Window | null) {
  const win = janela ?? window.open("", "_blank");
  if (!win) return;

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: [100, 150] });

  for (let vol = 1; vol <= totalVolumes; vol++) {
    if (vol > 1) doc.addPage([100, 150], "portrait");

    const qrText = `# (${pedido.id}) (${vol}) (${totalVolumes})`;
    let qrDataUrl = "";
    try {
      qrDataUrl = await QRCode.toDataURL(qrText, { width: 200, margin: 1 });
    } catch (_) {}

    doc.setDrawColor(51, 51, 51);
    doc.setLineWidth(0.35);
    doc.rect(2, 2, 96, 146);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.text(`VOLUME ${vol} / ${totalVolumes}`, 50, 15, { align: "center", maxWidth: 88 });
    doc.setLineWidth(0.7);
    doc.line(7, 20, 93, 20);

    doc.setFontSize(18);
    doc.text(`Pedido #${pedido.id}`, 50, 31, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(14);
    doc.text(
      doc.splitTextToSize(pedido.destinatarioFantasia || pedido.destinatarioRazao || "-", 84),
      50,
      42,
      { align: "center" },
    );

    if (qrDataUrl) doc.addImage(qrDataUrl, "PNG", 29, 60, 42, 42);

    doc.setFont("courier", "normal");
    doc.setFontSize(11);
    doc.setTextColor(85, 85, 85);
    doc.text(qrText, 50, 108, { align: "center" });

    doc.setTextColor(102, 102, 102);
    doc.setDrawColor(204, 204, 204);
    doc.setLineWidth(0.2);
    doc.line(7, 134, 93, 134);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text(
      doc.splitTextToSize(pedido.emitenteFantasia || pedido.emitenteNome || "-", 84),
      50,
      140,
      { align: "center" },
    );
    doc.setTextColor(0, 0, 0);
  }

  const pdfUrl = URL.createObjectURL(doc.output("blob"));
  win.location.href = pdfUrl;
  window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 60_000);
}

// ─── PDF Etiquetas de Volume (Finalizar Conferência — 10×15 cm) ───────────────
// Design baseado em boas práticas GS1 / padrão 4x6" (10x15cm):
// - Hierarquia visual clara: informação mais importante em maior destaque
// - Fontes grandes e legíveis para leitura em armazém
// - Separação visual entre seções (emitente / destinatário / volume / QR)
// - QR Code centralizado e de tamanho adequado para leitura por scanner

async function gerarEtiquetasVolume(pedido: any, totalVolumes: number, janela?: Window | null) {
  const win = janela ?? window.open("", "_blank");
  if (!win) return;

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: [100, 150] });

  for (let vol = 1; vol <= totalVolumes; vol++) {
    if (vol > 1) doc.addPage([100, 150], "portrait");

    const qrText = `PED#${pedido.id}-VOL${vol}/${totalVolumes}`;
    let qrDataUrl = "";
    try {
      qrDataUrl = await QRCode.toDataURL(qrText, { width: 280, margin: 1, errorCorrectionLevel: "M" });
    } catch (_) {}

    const destinatarioNome = pedido.destinatarioFantasia || pedido.destinatarioRazao || "-";
    const destinatarioEndereco = [
      pedido.destinatarioLogradouro,
      pedido.destinatarioNumero,
      pedido.destinatarioBairro,
    ].filter(Boolean).join(", ") || "";
    const destinatarioCidadeUf = [pedido.destinatarioCidade, pedido.destinatarioUf].filter(Boolean).join(" / ") || "";
    const destinatarioCep = pedido.destinatarioCep || "";
    const emitenteNome = pedido.emitenteFantasia || pedido.emitenteNome || "-";
    const emitenteCnpj = pedido.emitenteCnpj || "";

    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.rect(2, 2, 96, 146);

    doc.setFillColor(0, 0, 0);
    doc.rect(5, 5, 90, 15, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text("VOLUME", 14, 14);
    doc.setFontSize(30);
    doc.text(String(vol), 43, 16);
    doc.setFontSize(11);
    doc.setTextColor(204, 204, 204);
    doc.text("de", 59, 14);
    doc.setFontSize(20);
    doc.text(String(totalVolumes), 70, 16);

    doc.setTextColor(102, 102, 102);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("PEDIDO", 50, 26, { align: "center" });
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(22);
    doc.text(`#${pedido.id}`, 50, 35, { align: "center" });

    doc.setDrawColor(204, 204, 204);
    doc.setLineWidth(0.2);
    doc.line(7, 39, 93, 39);

    let y = 44;
    doc.setTextColor(136, 136, 136);
    doc.setFontSize(7);
    doc.text("DESTINATÁRIO", 7, y);
    y += 4;

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    const linhasDestinatario = doc.splitTextToSize(destinatarioNome, 86);
    doc.text(linhasDestinatario, 7, y);
    y += linhasDestinatario.length * 4.6 + 1;

    if (destinatarioEndereco) {
      doc.setTextColor(51, 51, 51);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      const linhasEndereco = doc.splitTextToSize(destinatarioEndereco, 86);
      doc.text(linhasEndereco, 7, y);
      y += linhasEndereco.length * 3.5 + 1;
    }

    if (destinatarioCidadeUf) {
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      const cidadeCep = `${destinatarioCidadeUf}${destinatarioCep ? ` — CEP ${destinatarioCep}` : ""}`;
      const linhasCidade = doc.splitTextToSize(cidadeCep, 86);
      doc.text(linhasCidade, 7, y);
      y += linhasCidade.length * 3.8 + 1;
    }

    const yEntrega = Math.max(y + 2, 72);
    doc.setDrawColor(204, 204, 204);
    doc.line(7, yEntrega - 3, 93, yEntrega - 3);
    doc.setTextColor(136, 136, 136);
    doc.setFontSize(7);
    doc.text("TIPO DE ENTREGA", 7, yEntrega);
    doc.text("TIPO DE FRETE", 54, yEntrega);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.text(doc.splitTextToSize(pedido.tipoEntrega || "-", 39), 7, yEntrega + 5);
    doc.text(doc.splitTextToSize(pedido.tipoFrete || "-", 39), 54, yEntrega + 5);

    doc.setDrawColor(204, 204, 204);
    doc.line(7, 87, 93, 87);
    if (qrDataUrl) doc.addImage(qrDataUrl, "PNG", 33, 91, 34, 34);
    doc.setFont("courier", "normal");
    doc.setTextColor(85, 85, 85);
    doc.setFontSize(7);
    doc.text(qrText, 50, 129, { align: "center" });

    doc.setDrawColor(0, 0, 0);
    doc.line(7, 134, 93, 134);
    doc.setTextColor(102, 102, 102);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.text("EMITENTE:", 7, 140);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    doc.text(doc.splitTextToSize(emitenteNome, 69), 25, 140);
    if (emitenteCnpj) {
      doc.setTextColor(85, 85, 85);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.text(`CNPJ: ${emitenteCnpj}`, 7, 147);
    }
    doc.setTextColor(0, 0, 0);
  }

  const pdfUrl = URL.createObjectURL(doc.output("blob"));
  win.location.href = pdfUrl;
  window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 60_000);
}

// ─── Componente Principal ─────────────────────────────────────────────────────

export default function OpenOrders() {
  const { user } = useGoogleAuth();
  const filtrosUrl = new URLSearchParams(window.location.search);
  const prazoUrl = filtrosUrl.get("prazo");
  const [filtroPrazo, setFiltroPrazo] = useState<"atraso" | "no-prazo" | null>(() =>
    prazoUrl === "atraso" || prazoUrl === "no-prazo"
      ? prazoUrl
      : null
  );
  const emitenteUrl = Number(filtrosUrl.get("emitenteId"));
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilters, setStatusFilters] = useState<Set<string>>(new Set());
  const [tipoEntregaFilters, setTipoEntregaFilters] = useState<Set<string>>(new Set());
  const [gnreStatusFilter, setGnreStatusFilter] = useState<"todos" | "sem_gnre" | "pendente" | "processado" | "processado_pago" | "erro">("todos");
  const [filtroEntreEmitentes, setFiltroEntreEmitentes] = useState(false);
  const [emitenteIdsTemporarios, setEmitenteIdsTemporarios] = useState<Set<string>>(() => Number.isInteger(emitenteUrl) && emitenteUrl > 0 ? new Set([String(emitenteUrl)]) : new Set());
  const [emitenteIdsAplicados, setEmitenteIdsAplicados] = useState<Set<string>>(() => Number.isInteger(emitenteUrl) && emitenteUrl > 0 ? new Set([String(emitenteUrl)]) : new Set());
  const [vendedorIdsTemporarios, setVendedorIdsTemporarios] = useState<Set<string>>(new Set());
  const [vendedorIdsAplicados, setVendedorIdsAplicados] = useState<Set<string>>(new Set());
  const [dataInicio, setDataInicio] = useState(() => filtrosUrl.get("dataInicio") || "");
  const [dataFim, setDataFim] = useState(() => filtrosUrl.get("dataFim") || "");
  const [sortColumn, setSortColumn] = useState("dataPedido");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 40;

  // Seleção de pedidos
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [modalSeparacaoMassaOpen, setModalSeparacaoMassaOpen] = useState(false);
  const [gerandoSeparacaoMassa, setGerandoSeparacaoMassa] = useState(false);

  // Modal detalhes
  const [detalhesPedidoId, setDetalhesPedidoId] = useState<number | null>(null);

  // Modal anotações
  const [anotacoesModalOpen, setAnotacoesModalOpen] = useState(false);
  const [anotacoesPedidoId, setAnotacoesPedidoId] = useState<number | null>(null);
  const [novaAnotacao, setNovaAnotacao] = useState("");
  const [anotacaoArquivos, setAnotacaoArquivos] = useState<File[]>([]);
  const [comprovantesGnre, setComprovantesGnre] = useState<File[]>([]);
  const [mencoesSelecionadas, setMencoesSelecionadas] = useState<number[]>([]);
  const [menuMencaoAberto, setMenuMencaoAberto] = useState(false);
  const [isEnviandoAnotacao, setIsEnviandoAnotacao] = useState(false);
  const [progressoAnotacao, setProgressoAnotacao] = useState<{ atual: number; total: number } | null>(null);
  const anotacaoFileInputRef = useRef<HTMLInputElement>(null);
  const comprovanteGnreFileInputRef = useRef<HTMLInputElement>(null);
  const [notificacoesAbertas, setNotificacoesAbertas] = useState(false);

  // Modal separar
  const [modalSepararOpen, setModalSepararOpen] = useState(false);
  const [separadoresSelecionados, setSeparadoresSelecionados] = useState<Set<number>>(new Set());
  const [buscaSeparadores, setBuscaSeparadores] = useState("");
  const [paginaSeparadores, setPaginaSeparadores] = useState(1);
  const [pontoOperacionalSelecionado, setPontoOperacionalSelecionado] = useState<number | null>(null);

  // Modal finalizar separação (confirmação)
  const [modalFinalizarSepOpen, setModalFinalizarSepOpen] = useState(false);

  // Modal conferir
  const [modalConferirOpen, setModalConferirOpen] = useState(false);
  const [conferentesSelecionados, setConferentesSelecionados] = useState<Set<number>>(new Set());
  const [volumesMistos, setVolumesMistos] = useState<number>(1);
  const [buscaConferentes, setBuscaConferentes] = useState("");
  const [paginaConferentes, setPaginaConferentes] = useState(1);

  // Modal finalizar conferência
  const [modalFinalizarConfOpen, setModalFinalizarConfOpen] = useState(false);
  const [volumesConferencia, setVolumesConferencia] = useState<number>(1);
  const [pesoBrutoConferencia, setPesoBrutoConferencia] = useState<number>(0);
  const [erroFinalizarConf, setErroFinalizarConf] = useState<string | null>(null);

  // Modal Solicitar Reposição
  const [modalReposicaoOpen, setModalReposicaoOpen] = useState(false);
  const [reposicaoPedidoId, setReposicaoPedidoId] = useState<number | null>(null);
  const [itensSelecionadosReposicao, setItensSelecionadosReposicao] = useState<Set<number>>(new Set());
  const [enderecoOrigemPorItem, setEnderecoOrigemPorItem] = useState<Record<number, string>>({});
  const [enderecoDestinoPorItem, setEnderecoDestinoPorItem] = useState<Record<number, string>>({});
  const [quantidadePorItem, setQuantidadePorItem] = useState<Record<number, string>>({});
  const [buscaDestinoPorItem, setBuscaDestinoPorItem] = useState<Record<number, string>>({});
  const [buscaOrigemPorItem, setBuscaOrigemPorItem] = useState<Record<number, string>>({});
  const [reposicaoErro, setReposicaoErro] = useState<string | null>(null);

  // Modal Retificar Pedido
  const [modalRetificarOpen, setModalRetificarOpen] = useState(false);
  const [retificarPedidoId, setRetificarPedidoId] = useState<number | null>(null);
  const [retificarItemId, setRetificarItemId] = useState<string>("");
  const [retificarModo, setRetificarModo] = useState<'excluir' | 'reduzir'>('reduzir');
  const [retificarQuantidade, setRetificarQuantidade] = useState<string>("");
  const [retificarMotivo, setRetificarMotivo] = useState("");
  const [retificarConfirmacaoFisica, setRetificarConfirmacaoFisica] = useState(false);
  const [modalRetificarVolumesOpen, setModalRetificarVolumesOpen] = useState(false);
  const [volumesRetificacao, setVolumesRetificacao] = useState<number>(1);
  const [modalRetificarVolumesMistosOpen, setModalRetificarVolumesMistosOpen] = useState(false);
  const [volumesMistosRetificacao, setVolumesMistosRetificacao] = useState<number>(1);

  // Modal Consultar Reposições
  const [modalConsultaReposicaoOpen, setModalConsultaReposicaoOpen] = useState(false);
  const [consultaReposicaoPedidoId, setConsultaReposicaoPedidoId] = useState<number | null>(null);

  const utils = trpc.useUtils();

  const { data: pedidos = [], isLoading } = trpc.pedidos.listOpen.useQuery();
  const { data: usuarios = [] } = trpc.pedidos.listUsuarios.useQuery();

  const statusDisponiveis = Array.from(new Set(
    pedidos.map((pedido: any) => pedido.status).filter(Boolean)
  )).sort((a, b) => a.localeCompare(b, "pt-BR"));

  const tiposEntregaDisponiveis = Array.from(new Set(
    pedidos.map((pedido: any) => pedido.tipoEntrega).filter(Boolean)
  )).sort((a, b) => a.localeCompare(b, "pt-BR"));

  const emitentesDisponiveis = Array.from(new Map(
    pedidos
      .filter((pedido: any) => pedido.emitenteId !== null && pedido.emitenteId !== undefined)
      .map((pedido: any) => [
        String(pedido.emitenteId),
        pedido.emitenteFantasia || pedido.emitenteNome || `Emitente #${pedido.emitenteId}`,
      ])
  ).entries())
    .map(([id, nome]) => ({ id, nome }))
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

  const todosEmitentesSelecionados = emitentesDisponiveis.length > 0 &&
    emitentesDisponiveis.every((emitente) => emitenteIdsTemporarios.has(emitente.id));

  const pedidosDosEmitentesAplicados = pedidos.filter((pedido: any) =>
    emitenteIdsAplicados.size === 0 || emitenteIdsAplicados.has(String(pedido.emitenteId))
  );

  const vendedoresDisponiveis = Array.from(new Map(
    pedidosDosEmitentesAplicados.map((pedido: any) => {
      const id = pedido.vendedorId === null || pedido.vendedorId === undefined
        ? SEM_VENDEDOR_ID
        : String(pedido.vendedorId);
      const nome = id === SEM_VENDEDOR_ID
        ? "Sem vendedor"
        : pedido.vendedorNome || `Vendedor #${pedido.vendedorId}`;
      return [id, nome];
    })
  ).entries())
    .map(([id, nome]) => ({ id, nome }))
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

  const todosVendedoresSelecionados = vendedoresDisponiveis.length > 0 &&
    vendedoresDisponiveis.every((vendedor) => vendedorIdsTemporarios.has(vendedor.id));

  // Detalhes do pedido (lazy — só busca quando o modal abre)
  const { data: pedidoDetalhes, isLoading: loadingDetalhes } = trpc.pedidos.getDetalhes.useQuery(
    { id: detalhesPedidoId! },
    { enabled: detalhesPedidoId !== null }
  );

  const { data: anotacoesDetalhes } = trpc.pedidos.getDetalhes.useQuery(
    { id: anotacoesPedidoId! },
    { enabled: anotacoesPedidoId !== null }
  );
  const { data: anotacoesList = [], refetch: refetchAnotacoes } = trpc.pedidos.listAnotacoes.useQuery(
    { pedidoId: anotacoesPedidoId! },
    { enabled: anotacoesPedidoId !== null }
  );
  const { data: participantesAnotacoes = [] } = trpc.pedidos.participantesAnotacoes.useQuery(
    { pedidoId: anotacoesPedidoId! },
    { enabled: anotacoesPedidoId !== null }
  );
  const addAnotacaoMutation = trpc.pedidos.addAnotacao.useMutation({
    onSuccess: () => {
      setNovaAnotacao("");
      setAnotacaoArquivos([]);
      setComprovantesGnre([]);
      setMencoesSelecionadas([]);
      setMenuMencaoAberto(false);
      refetchAnotacoes();
      utils.pedidos.listOpen.invalidate();
    },
    onError: (err) => toast.error(err.message || "Erro ao adicionar anotação"),
  });
  const { data: notificacoesData } = trpc.pedidos.notificacoes.list.useQuery({ page: 1, pageSize: 20 });
  const notificacoes = (notificacoesData?.data as any[]) ?? [];
  const notificacoesNaoLidas = Number(notificacoesData?.naoLidas ?? 0);
  const marcarNotificacaoLidaMutation = trpc.pedidos.notificacoes.marcarLida.useMutation({
    onSuccess: () => utils.pedidos.notificacoes.list.invalidate(),
    onError: (err) => toast.error(err.message || "Erro ao atualizar notificação"),
  });

  const abrirAnotacoesPedido = (pedidoId: number) => {
    setAnotacoesPedidoId(pedidoId);
    setAnotacoesModalOpen(true);
    const url = new URL(window.location.href);
    url.searchParams.set("anotacoesPedido", String(pedidoId));
    window.history.replaceState(null, "", url.toString());
  };

  const fecharAnotacoesPedido = () => {
    setAnotacoesModalOpen(false);
    setAnotacoesPedidoId(null);
    setNovaAnotacao("");
    setAnotacaoArquivos([]);
    setComprovantesGnre([]);
    setMencoesSelecionadas([]);
    setMenuMencaoAberto(false);
    const url = new URL(window.location.href);
    url.searchParams.delete("anotacoesPedido");
    window.history.replaceState(null, "", url.toString());
  };

  useEffect(() => {
    const pedidoId = Number(new URLSearchParams(window.location.search).get("anotacoesPedido"));
    if (Number.isInteger(pedidoId) && pedidoId > 0) abrirAnotacoesPedido(pedidoId);
  }, []);

  const obterSugestoesMencao = (texto: string) => {
    const correspondencia = texto.match(/(?:^|\s)@([^\s@]*)$/);
    if (!correspondencia) return [];
    const buscaMencao = correspondencia[1].toLowerCase();
    return (participantesAnotacoes as any[]).filter((participante: any) => Number(participante.id) !== Number(user?.id) && (participante.name || "").toLowerCase().includes(buscaMencao));
  };

  const inserirMencaoNoTexto = (texto: string, nome: string) => texto.replace(/@([^\s@]*)$/, `@${nome} `);

  const adicionarArquivosAnotacao = (arquivos: File[], categoria: "anexo" | "comprovante_gnre" = "anexo") => {
    const arquivoGrande = arquivos.find((arquivo) => arquivo.size > 50 * 1024 * 1024);
    if (arquivoGrande) return toast.error(`${arquivoGrande.name} excede 50 MB`);
    if (categoria === "comprovante_gnre") {
      setComprovantesGnre((atuais) => [...atuais, ...arquivos]);
      return;
    }
    setAnotacaoArquivos((atuais) => [...atuais, ...arquivos]);
  };

  const handleEnviarAnotacao = async () => {
    const totalArquivos = anotacaoArquivos.length + comprovantesGnre.length;
    if (!anotacoesPedidoId || (!novaAnotacao.trim() && totalArquivos === 0)) return;
    setIsEnviandoAnotacao(true);
    setProgressoAnotacao({ atual: 0, total: totalArquivos });
    try {
      const arquivosEnviados: Array<{ nome: string; url: string; tamanho: number; tipoMime?: string; categoria: "anexo" | "comprovante_gnre" }> = [];
      let arquivosEnviadosTotal = 0;
      const enviarArquivo = async (arquivo: File, categoria: "anexo" | "comprovante_gnre") => {
        const formData = new FormData();
        formData.append("file", arquivo);
        const resposta = await fetch("/api/upload", { method: "POST", body: formData, credentials: "include" });
        if (!resposta.ok) throw new Error(`Falha ao enviar ${arquivo.name}`);
        const dados = await resposta.json();
        arquivosEnviados.push({ nome: arquivo.name, url: dados.url, tamanho: arquivo.size, tipoMime: arquivo.type || undefined, categoria });
        arquivosEnviadosTotal += 1;
        setProgressoAnotacao({ atual: arquivosEnviadosTotal, total: totalArquivos });
      };
      for (const arquivo of anotacaoArquivos) {
        await enviarArquivo(arquivo, "anexo");
      }
      for (const arquivo of comprovantesGnre) {
        await enviarArquivo(arquivo, "comprovante_gnre");
      }
      const mencoes = mencoesSelecionadas.filter((id) => {
        const participante = (participantesAnotacoes as any[]).find((item: any) => Number(item.id) === Number(id));
        return participante?.name && novaAnotacao.includes(`@${participante.name}`);
      });
      await addAnotacaoMutation.mutateAsync({ pedidoId: anotacoesPedidoId, texto: novaAnotacao.trim(), mencoes, arquivos: arquivosEnviados });
    } catch (erro: any) {
      toast.error(erro?.message || "Falha no upload dos arquivos da anotação. Os arquivos permanecem prontos para nova tentativa.");
    } finally {
      setIsEnviandoAnotacao(false);
      setProgressoAnotacao(null);
    }
  };

  // Detalhes do pedido para modal de retificação
  const { data: pedidoRetificacaoDetalhes, isLoading: loadingRetificacaoDetalhes } = trpc.pedidos.getDetalhes.useQuery(
    { id: retificarPedidoId! },
    { enabled: retificarPedidoId !== null && modalRetificarOpen }
  );

  // Detalhes do pedido para modal de reposição
  const { data: pedidoReposicaoDetalhes, isLoading: loadingReposicaoDetalhes } = trpc.pedidos.getDetalhes.useQuery(
    { id: reposicaoPedidoId! },
    { enabled: reposicaoPedidoId !== null && modalReposicaoOpen }
  );

  // Lista de endereços cadastrados para o select de destino no modal de reposição
  const { data: enderecosCadastrados = [] } = trpc.addresses.list.useQuery(undefined, { enabled: modalReposicaoOpen });

  // Reposições do pedido para modal de consulta
  const { data: reposicoesPedido = [], isLoading: loadingReposicoes } = trpc.pedidos.getReposicoes.useQuery(
    { pedidoId: consultaReposicaoPedidoId! },
    { enabled: consultaReposicaoPedidoId !== null && modalConsultaReposicaoOpen }
  );

  const mutSolicitarReposicao = trpc.pedidos.solicitarReposicao.useMutation({
    onSuccess: () => {
      setModalReposicaoOpen(false);
      setReposicaoPedidoId(null);
      setItensSelecionadosReposicao(new Set());
      setEnderecoOrigemPorItem({});
      setEnderecoDestinoPorItem({});
      setReposicaoErro(null);
    },
    onError: (err) => {
      setReposicaoErro(err.message || 'Erro ao solicitar reposição');
    },
  });

  const mutRetificarItem = trpc.pedidos.retificarItem.useMutation({
    onSuccess: () => {
      utils.pedidos.listOpen.invalidate();
      if (detalhesPedidoId !== null) utils.pedidos.getDetalhes.invalidate({ id: detalhesPedidoId });
      setModalRetificarOpen(false);
      setRetificarPedidoId(null);
      setRetificarItemId("");
      setRetificarQuantidade("");
      setRetificarMotivo("");
      setRetificarConfirmacaoFisica(false);
      setSelectedIds(new Set());
      toast.success("Pedido retificado com sucesso");
    },
    onError: (error) => toast.error(error.message || "Erro ao retificar pedido"),
  });

  const mutRetificarVolumes = trpc.pedidos.retificarVolumes.useMutation({
    onSuccess: () => {
      utils.pedidos.listOpen.invalidate();
      setModalRetificarVolumesOpen(false);
      setVolumesRetificacao(1);
      toast.success("Volumes retificados com sucesso");
    },
    onError: (error) => toast.error(error.message || "Erro ao retificar volumes"),
  });

  const mutRetificarVolumesMistos = trpc.pedidos.retificarVolumesMistos.useMutation({
    onSuccess: () => {
      utils.pedidos.listOpen.invalidate();
      setModalRetificarVolumesMistosOpen(false);
      setVolumesMistosRetificacao(1);
      toast.success("Volumes mistos retificados com sucesso");
    },
    onError: (error) => toast.error(error.message || "Erro ao retificar volumes mistos"),
  });

  const mutIniciarSeparacao = trpc.pedidos.iniciarSeparacao.useMutation({
    onSuccess: () => { utils.pedidos.listOpen.invalidate(); setModalSepararOpen(false); setSeparadoresSelecionados(new Set()); setSelectedIds(new Set()); setPontoOperacionalSelecionado(null); },
  });
  const { data: pontosOperacionais } = trpc.pontosOperacionais.list.useQuery();
  const mutFinalizarSeparacao = trpc.pedidos.finalizarSeparacao.useMutation({
    onSuccess: () => { utils.pedidos.listOpen.invalidate(); setModalFinalizarSepOpen(false); setSelectedIds(new Set()); },
  });
  const mutIniciarConferencia = trpc.pedidos.iniciarConferencia.useMutation({
    onSuccess: async () => {
      utils.pedidos.listOpen.invalidate();
      setModalConferirOpen(false);
      setConferentesSelecionados(new Set());
      // Gerar etiquetas para cada pedido selecionado
      for (const id of selectedIds) {
        const det = await utils.pedidos.getDetalhes.fetch({ id });
        if (det) await gerarEtiquetasVolumeMisto(det, volumesMistos);
      }
      setSelectedIds(new Set());
    },
  });

  // Mutação: Finalizar Conferência
  const mutFinalizarConferencia = trpc.pedidos.finalizarConferencia.useMutation({
    onSuccess: async (data) => {
      utils.pedidos.listOpen.invalidate();
      setModalFinalizarConfOpen(false);
      setErroFinalizarConf(null);
      // Abrir PDF das etiquetas de volume em nova aba
      const pedidoId = Array.from(selectedIds)[0];
      const det = await utils.pedidos.getDetalhes.fetch({ id: pedidoId });
      if (det) await gerarEtiquetasVolume(det, volumesConferencia);
      setSelectedIds(new Set());
      setVolumesConferencia(1);
    },
    onError: (error) => {
      // O backend já mudou o status para "em revisão"
      utils.pedidos.listOpen.invalidate();
      setErroFinalizarConf(error.message || "Ocorreu um erro durante o processo de faturamento.");
    },
  });

  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds(new Set());
  }, [searchTerm, statusFilters, tipoEntregaFilters, gnreStatusFilter, filtroEntreEmitentes, emitenteIdsAplicados, vendedorIdsAplicados, dataInicio, dataFim]);

  function toggleStatusFilter(status: string) {
    setStatusFilters((anteriores) => {
      const proximos = new Set(anteriores);
      if (proximos.has(status)) proximos.delete(status);
      else proximos.add(status);
      return proximos;
    });
  }

  function toggleTipoEntregaFilter(tipoEntrega: string) {
    setTipoEntregaFilters((anteriores) => {
      const proximos = new Set(anteriores);
      if (proximos.has(tipoEntrega)) proximos.delete(tipoEntrega);
      else proximos.add(tipoEntrega);
      return proximos;
    });
  }

  function toggleSort(column: string) {
    if (sortColumn === column) {
      setSortDirection((direcao) => direcao === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setCurrentPage(1);
    setSelectedIds(new Set());
  }

  function getSortValue(pedido: any, column: string): string | number {
    switch (column) {
      case "numeroPedido": return Number(pedido.id || 0);
      case "numeroNfe": return Number(pedido.numeroNfe || 0);
      case "dataPedido": return pedido.dataPedido ? new Date(pedido.dataPedido.toString().includes("T") ? pedido.dataPedido : pedido.dataPedido + "T00:00:00").getTime() : 0;
      case "dataFaturamento": return pedido.dataFaturamento ? new Date(pedido.dataFaturamento.toString().includes("T") ? pedido.dataFaturamento : pedido.dataFaturamento + "T00:00:00").getTime() : 0;
      case "valorTotal": return Number(pedido.valorTotal || 0);
      case "emitente": return pedido.emitenteFantasia || pedido.emitenteNome || "";
      case "diasEmAberto": return calcularDiasEmAberto(pedido.dataPedido);
      case "etiqueta": return pedido.etiqueta || pedido.tag || pedido.orcamentoTag || "";
      default: return pedido[column] || "";
    }
  }

  function toggleEmitenteTemporario(emitenteId: string) {
    setEmitenteIdsTemporarios((anteriores) => {
      const proximos = new Set(anteriores);
      if (proximos.has(emitenteId)) proximos.delete(emitenteId);
      else proximos.add(emitenteId);
      return proximos;
    });
  }

  function toggleTodosEmitentes() {
    setEmitenteIdsTemporarios(
      todosEmitentesSelecionados
        ? new Set()
        : new Set(emitentesDisponiveis.map((emitente) => emitente.id))
    );
  }

  function aplicarFiltroEmitentes() {
    const novosEmitentes = new Set(emitenteIdsTemporarios);
    const vendedoresPermitidos = new Set(pedidos
      .filter((pedido: any) => novosEmitentes.size === 0 || novosEmitentes.has(String(pedido.emitenteId)))
      .map((pedido: any) => pedido.vendedorId === null || pedido.vendedorId === undefined ? SEM_VENDEDOR_ID : String(pedido.vendedorId)));
    setEmitenteIdsAplicados(novosEmitentes);
    setVendedorIdsTemporarios((anteriores) => new Set(Array.from(anteriores).filter((id) => vendedoresPermitidos.has(id))));
    setVendedorIdsAplicados((anteriores) => new Set(Array.from(anteriores).filter((id) => vendedoresPermitidos.has(id))));
  }

  function toggleVendedorTemporario(vendedorId: string) {
    setVendedorIdsTemporarios((anteriores) => {
      const proximos = new Set(anteriores);
      if (proximos.has(vendedorId)) proximos.delete(vendedorId);
      else proximos.add(vendedorId);
      return proximos;
    });
  }

  function toggleTodosVendedores() {
    setVendedorIdsTemporarios(
      todosVendedoresSelecionados
        ? new Set()
        : new Set(vendedoresDisponiveis.map((vendedor) => vendedor.id))
    );
  }

  function aplicarFiltroVendedores() {
    setVendedorIdsAplicados(new Set(vendedorIdsTemporarios));
  }

  // Filtro e paginação de usuários — Separadores
  const usuariosPorPagina = 8;
  const usuariosFiltradosSep = usuarios.filter((u: any) =>
    u.name?.toLowerCase().includes(buscaSeparadores.toLowerCase())
  );
  const totalPaginasSep = Math.ceil(usuariosFiltradosSep.length / usuariosPorPagina);
  const usuariosPaginadosSep = usuariosFiltradosSep.slice(
    (paginaSeparadores - 1) * usuariosPorPagina,
    paginaSeparadores * usuariosPorPagina
  );

  // Filtro e paginação de usuários — Conferentes
  const usuariosFiltradosConf = usuarios.filter((u: any) =>
    u.name?.toLowerCase().includes(buscaConferentes.toLowerCase())
  );
  const totalPaginasConf = Math.ceil(usuariosFiltradosConf.length / usuariosPorPagina);
  const usuariosPaginadosConf = usuariosFiltradosConf.slice(
    (paginaConferentes - 1) * usuariosPorPagina,
    paginaConferentes * usuariosPorPagina
  );

  const dataInicioTimestamp = dataInicio ? new Date(`${dataInicio}T00:00:00`).getTime() : null;
  const dataFimTimestamp = dataFim ? new Date(`${dataFim}T23:59:59.999`).getTime() : null;
  const agora = Date.now();

  function obterChaveStatusGnre(pedido: any) {
    if (pedido.gnreStatus === "processado" && pedido.possuiComprovanteGnre) return "processado_pago";
    return pedido.gnreStatus || "sem_gnre";
  }

  const filteredPedidos = pedidos.filter((pedido: any) => {
    const search = searchTerm.toLowerCase();
    const correspondeBusca =
      pedido.id?.toString().includes(search) ||
      pedido.referenciaBox?.toString().includes(search) ||
      pedido.destinatarioNome?.toLowerCase().includes(search) ||
      pedido.status?.toLowerCase().includes(search) ||
      pedido.criadoPorNome?.toLowerCase().includes(search) ||
      pedido.destinatarioCidade?.toLowerCase().includes(search);
    const correspondeStatus = statusFilters.size === 0 || statusFilters.has(pedido.status);
    const correspondeTipoEntrega = tipoEntregaFilters.size === 0 || tipoEntregaFilters.has(pedido.tipoEntrega);
    const correspondeGnre = gnreStatusFilter === "todos" || obterChaveStatusGnre(pedido) === gnreStatusFilter;
    const correspondeEntreEmitentes = !filtroEntreEmitentes ||
      (pedido.destinatarioEmitenteId !== null && pedido.destinatarioEmitenteId !== undefined);
    const correspondeEmitente = emitenteIdsAplicados.size === 0 ||
      emitenteIdsAplicados.has(String(pedido.emitenteId));
    const vendedorId = pedido.vendedorId === null || pedido.vendedorId === undefined
      ? SEM_VENDEDOR_ID
      : String(pedido.vendedorId);
    const correspondeVendedor = vendedorIdsAplicados.size === 0 || vendedorIdsAplicados.has(vendedorId);
    const dataPedidoTimestamp = pedido.dataPedido
      ? new Date(pedido.dataPedido.toString().includes("T") ? pedido.dataPedido : pedido.dataPedido + "T00:00:00").getTime()
      : null;
    const correspondeDataInicio = dataInicioTimestamp === null ||
      (dataPedidoTimestamp !== null && dataPedidoTimestamp >= dataInicioTimestamp);
    const correspondeDataFim = dataFimTimestamp === null ||
      (dataPedidoTimestamp !== null && dataPedidoTimestamp <= dataFimTimestamp);
    const statusPrazo = calcularPrazoPedido(pedido, agora)?.status;
    const correspondePrazo = filtroPrazo === null ||
      (filtroPrazo === "atraso" && statusPrazo === "Em atraso") ||
      (filtroPrazo === "no-prazo" && statusPrazo === "No prazo");

    return correspondeBusca && correspondeStatus && correspondeTipoEntrega && correspondeGnre && correspondeEntreEmitentes && correspondeEmitente && correspondeVendedor && correspondeDataInicio && correspondeDataFim && correspondePrazo;
  });

  const sortedPedidos = [...filteredPedidos].sort((a: any, b: any) => {
    const valorA = getSortValue(a, sortColumn);
    const valorB = getSortValue(b, sortColumn);
    const comparacao = typeof valorA === "number" && typeof valorB === "number"
      ? valorA - valorB
      : String(valorA).localeCompare(String(valorB), "pt-BR", { numeric: true, sensitivity: "base" });
    return sortDirection === "asc" ? comparacao : -comparacao;
  });

  const totalPages = Math.ceil(sortedPedidos.length / itemsPerPage);
  const paginatedPedidos = sortedPedidos.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const allPageSelected = paginatedPedidos.length > 0 && paginatedPedidos.every((p: any) => selectedIds.has(p.id));
  const { data: etiquetasPedidos = {} as Record<number, any[]> } = trpc.etiquetas.porEntidades.useQuery({
    modulo: "pedido",
    entidadeIds: paginatedPedidos.map((pedido: any) => pedido.id),
  });

  const pedidoColumns: ReorderableColumn<any>[] = [
    {
      id: "selecionar",
      header: <Checkbox checked={allPageSelected} onCheckedChange={toggleSelectAll} aria-label="Selecionar todos" />,
      renderCell: (pedido) => <Checkbox checked={selectedIds.has(pedido.id)} onCheckedChange={() => toggleSelect(pedido.id)} />,
      fixed: true,
      headerClassName: "w-10 p-2 text-left text-sm font-medium",
    },
    {
      id: "numeroPedido",
      header: "Pedido",
      renderCell: (pedido) => <span className="inline-flex items-center gap-2">#{pedido.id}{pedido.destinatarioEmitenteId !== null && pedido.destinatarioEmitenteId !== undefined && <span className="rounded bg-violet-100 px-1.5 py-0.5 text-xs font-medium text-violet-800" title="CNPJ do destinatário encontrado na tabela de emitentes">Entre emitente</span>}</span>,
      className: "p-2 font-medium",
      sortKey: "numeroPedido",
    },
    { id: "numeroNfe", header: "Número da Nota", renderCell: (pedido) => pedido.numeroNfe || "-", sortKey: "numeroNfe" },
    { id: "referenciaBox", header: "Referência da Importação", renderCell: (pedido) => pedido.referenciaBox || "-", sortKey: "referenciaBox" },
    { id: "emitente", header: "Emitente", renderCell: (pedido) => pedido.emitenteFantasia || pedido.emitenteNome || "-", sortKey: "emitente" },
    { id: "dataPedido", header: "Data", renderCell: (pedido) => formatDate(pedido.dataPedido), sortKey: "dataPedido" },
    { id: "dataFaturamento", header: "Data de Faturamento", renderCell: (pedido) => formatDate(pedido.dataFaturamento), sortKey: "dataFaturamento" },
    { id: "destinatarioNome", header: "Destinatário", renderCell: (pedido) => pedido.destinatarioNome, sortKey: "destinatarioNome" },
    { id: "destinatarioRazao", header: "Razão Social", renderCell: (pedido) => pedido.destinatarioRazao || "-", sortKey: "destinatarioRazao" },
    { id: "valorTotal", header: "Valor", renderCell: (pedido) => <>R$ {pedido.valorTotal?.toFixed(2) || "0.00"}</>, sortKey: "valorTotal" },
    { id: "status", header: "Status", renderCell: (pedido) => <><StatusBadge status={pedido.status} /><AvisoStatusPedido status={pedido.status} /></>, sortKey: "status" },
    { id: "etiqueta", header: "Etiqueta", renderCell: (pedido) => <EtiquetasChips etiquetas={etiquetasPedidos[pedido.id]} />, sortKey: "etiqueta" },
    {
      id: "diasEmAberto",
      header: "Dias em Aberto",
      renderCell: (pedido) => <span className={`px-2 py-1 rounded text-xs font-medium ${calcularDiasEmAberto(pedido.dataPedido) > 30 ? "bg-red-100 text-red-800" : calcularDiasEmAberto(pedido.dataPedido) > 15 ? "bg-yellow-100 text-yellow-800" : "bg-green-100 text-green-800"}`}>{calcularDiasEmAberto(pedido.dataPedido)} dias</span>,
      sortKey: "diasEmAberto",
    },
    { id: "criadoPorNome", header: "Criado por", renderCell: (pedido) => pedido.criadoPorNome || "-", className: "p-2 text-sm text-muted-foreground", sortKey: "criadoPorNome" },
    {
      id: "acoes",
      header: "Ações",
      renderCell: (pedido) => <div className="flex items-center gap-1">
        <Button variant="ghost" size="sm" onClick={() => setDetalhesPedidoId(pedido.id)} title="Ver detalhes do pedido"><Eye className="h-4 w-4" /></Button>
        <Button variant="ghost" size="sm" onClick={() => abrirAnotacoesPedido(pedido.id)} title={pedido.possuiAnotacaoManual ? "Há anotação manual" : "Anotações"} className={`p-1 ${pedido.possuiAnotacaoManual ? "text-[#00e5ff] hover:text-[#00b8d4]" : "hover:text-blue-600"}`}><MessageSquare className="h-4 w-4" /></Button>
        <Button variant="ghost" size="sm" onClick={() => { setConsultaReposicaoPedidoId(pedido.id); setModalConsultaReposicaoOpen(true); }} title="Ver reposições em andamento" className="text-orange-500 hover:text-orange-700"><Flag className="h-4 w-4" /></Button>
      </div>,
    },
  ];

  function toggleSelectAll() {
    if (allPageSelected) {
      const next = new Set(selectedIds);
      paginatedPedidos.forEach((p: any) => next.delete(p.id));
      setSelectedIds(next);
    } else {
      const next = new Set(selectedIds);
      paginatedPedidos.forEach((p: any) => next.add(p.id));
      setSelectedIds(next);
    }
  }

  function toggleSelect(id: number) {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  }

  const nSelecionados = selectedIds.size;

  // Verificar se o pedido selecionado está em conferência (para habilitar o botão)
  const pedidoSelecionadoEmConferencia = nSelecionados === 1 &&
    pedidos.some((p: any) => selectedIds.has(p.id) && p.status === "em conferencia");

  // Verificar se o pedido selecionado pode ter reposição (status antes de 'para conferencia')
  const statusAntesConferencia = ["pendente", "em separação"];
  const pedidoSelecionadoParaReposicao = nSelecionados === 1 &&
    pedidos.some((p: any) => selectedIds.has(p.id) && statusAntesConferencia.includes(p.status));

  // Verificar se algum pedido selecionado está em revisão (bloqueia Separar e Conferir)
  const algumSelecionadoEmRevisao = nSelecionados > 0 &&
    pedidos.some((p: any) => selectedIds.has(p.id) && p.status === 'em revisão');

  const algumSelecionadoEmConferencia = nSelecionados > 0 &&
    pedidos.some((p: any) => selectedIds.has(p.id) && p.status === 'em conferencia');

  const pedidoSelecionadoRetificavel = user?.role === 'admin' && nSelecionados === 1 &&
    pedidos.some((p: any) => selectedIds.has(p.id) && ['pendente', 'em separação', 'em conferencia'].includes(p.status));

  const pedidoSelecionadoReimprimirEtiquetaVolume = nSelecionados === 1 &&
    pedidos.some((p: any) => selectedIds.has(p.id) && ['em revisão', 'solicitar expedição', 'aguardando expedição'].includes(p.status));

  const pedidoSelecionadoRetificarVolumes = pedidoSelecionadoReimprimirEtiquetaVolume;

  const pedidoSelecionadoReimprimirEtiquetaMisto = nSelecionados === 1 &&
    pedidos.some((p: any) => selectedIds.has(p.id) && ['em conferencia', 'solicitar expedição', 'aguardando expedição', 'aguardando finalização', 'finalizado'].includes(p.status));

  const pedidoSelecionadoRetificarVolumesMistos = pedidoSelecionadoReimprimirEtiquetaMisto;

  const algumSelecionadoSolicitarExpedicao = nSelecionados > 0 &&
    pedidos.some((p: any) => selectedIds.has(p.id) && p.status === 'solicitar expedição');

  function handleAbrirRetificacao() {
    const pedidoId = Array.from(selectedIds)[0];
    setRetificarPedidoId(pedidoId);
    setRetificarItemId("");
    setRetificarModo('reduzir');
    setRetificarQuantidade("");
    setRetificarMotivo("");
    setRetificarConfirmacaoFisica(false);
    setModalRetificarOpen(true);
  }

  function handleConfirmarRetificacao() {
    if (!pedidoRetificacaoDetalhes || !retificarItemId || !retificarMotivo.trim()) {
      toast.error("Selecione o item e informe o motivo da retificação");
      return;
    }
    const item = (pedidoRetificacaoDetalhes.itens || []).find((registro: any) => String(registro.id) === retificarItemId);
    if (!item) {
      toast.error("Item selecionado não encontrado no pedido");
      return;
    }
    const quantidadeAtual = Number(item.quantidade);
    const novaQuantidade = Number(retificarQuantidade);
    if (retificarModo === 'reduzir' && (!Number.isFinite(novaQuantidade) || novaQuantidade <= 0 || novaQuantidade >= quantidadeAtual)) {
      toast.error("A nova quantidade deve ser maior que zero e menor que a quantidade atual");
      return;
    }
    if (pedidoRetificacaoDetalhes.status !== 'pendente' && !retificarConfirmacaoFisica) {
      toast.error("Confirme que a quantidade removida será devolvida fisicamente aos endereços indicados");
      return;
    }
    mutRetificarItem.mutate({
      pedidoId: pedidoRetificacaoDetalhes.id,
      pedidoItemId: Number(retificarItemId),
      modo: retificarModo,
      novaQuantidade: retificarModo === 'reduzir' ? novaQuantidade : undefined,
      motivo: retificarMotivo.trim(),
    });
  }

  function handleAbrirRetificarVolumes() {
    const pedido = pedidos.find((registro: any) => selectedIds.has(registro.id));
    setVolumesRetificacao(Math.max(1, Number(pedido?.volumes) || 1));
    setModalRetificarVolumesOpen(true);
  }

  function handleConfirmarRetificarVolumes() {
    const pedidoId = Array.from(selectedIds)[0];
    mutRetificarVolumes.mutate({ pedidoId, volumes: volumesRetificacao });
  }

  function handleAbrirRetificarVolumesMistos() {
    const pedido = pedidos.find((registro: any) => selectedIds.has(registro.id));
    setVolumesMistosRetificacao(Math.max(1, Number(pedido?.volumesMistos) || 1));
    setModalRetificarVolumesMistosOpen(true);
  }

  function handleConfirmarRetificarVolumesMistos() {
    const pedidoId = Array.from(selectedIds)[0];
    mutRetificarVolumesMistos.mutate({ pedidoId, volumesMistos: volumesMistosRetificacao });
  }

  function handleAbrirReposicao() {
    const id = Array.from(selectedIds)[0];
    setReposicaoPedidoId(id);
    setItensSelecionadosReposicao(new Set());
    setEnderecoOrigemPorItem({});
    setEnderecoDestinoPorItem({});
    setQuantidadePorItem({});
    setBuscaDestinoPorItem({});
    setBuscaOrigemPorItem({});
    setReposicaoErro(null);
    setModalReposicaoOpen(true);
  }

  async function handleConfirmarReposicao() {
    if (!pedidoReposicaoDetalhes || itensSelecionadosReposicao.size === 0) return;
    setReposicaoErro(null);

    const itens = Array.from(itensSelecionadosReposicao).map((itemId) => {
      const item = (pedidoReposicaoDetalhes.itens || []).find((i: any) => i.id === itemId);
      return {
        itemId,
        productCod: item?.productCod || '',
        quantidade: parseFloat(quantidadePorItem[itemId] ?? item?.quantidade ?? '1'),
        enderecoOrigem: enderecoOrigemPorItem[itemId] || '',
        enderecoDestino: enderecoDestinoPorItem[itemId] || '',
      };
    });

    const semOrigem = itens.filter(i => !i.enderecoOrigem);
    const semDestino = itens.filter(i => !i.enderecoDestino);
    if (semOrigem.length > 0) { setReposicaoErro('Selecione o endereço de origem (palete) para todos os itens.'); return; }
    if (semDestino.length > 0) { setReposicaoErro('Selecione o endereço de destino (picking) para todos os itens.'); return; }

    mutSolicitarReposicao.mutate({ pedidoId: reposicaoPedidoId!, itens });
  }

  // ─── Ações ──────────────────────────────────────────────────────────────────

  function handleSepararConfirm() {
    const seps = usuarios
      .filter((u: any) => separadoresSelecionados.has(u.id))
      .map((u: any) => ({ id: u.id, nome: u.name }));
    if (!pontoOperacionalSelecionado) return;

    const pedidoIds = Array.from(selectedIds);
    const win = window.open("", "_blank");
    if (!win) return;

    mutIniciarSeparacao.mutate({ pedidoIds, separadores: seps, pontoOperacionalId: pontoOperacionalSelecionado });
    void Promise.all(pedidoIds.map((id) => utils.pedidos.getDetalhes.fetch({ id })))
      .then((detalhes) => {
        const pedidosDetalhados = detalhes.filter(Boolean);
        if (pedidosDetalhados.length === 0) {
          win.close();
          return;
        }
        gerarPDFSeparacaoLote(pedidosDetalhados, win);
      })
      .catch(() => win.close());
  }

  async function handleGerarSeparacaoMassa(formato: "pdf" | "excel") {
    const pedidoIds = Array.from(selectedIds);
    if (pedidoIds.length === 0) return;

    const win = formato === "pdf" ? window.open("", "_blank") : null;
    if (formato === "pdf" && !win) return;

    setGerandoSeparacaoMassa(true);
    try {
      const detalhes = await Promise.all(pedidoIds.map((id) => utils.pedidos.getDetalhes.fetch({ id })));
      const itens = consolidarItensSeparacaoMassa(detalhes.filter(Boolean));

      if (itens.length === 0) {
        win?.close();
        toast.error("Os pedidos selecionados não possuem itens para separação.");
        return;
      }

      if (formato === "pdf" && win) {
        gerarPdfSeparacaoMassa(itens, detalhes.filter(Boolean), win);
      } else {
        gerarExcelSeparacaoMassa(itens);
      }
      setModalSeparacaoMassaOpen(false);
    } catch {
      win?.close();
      toast.error("Não foi possível gerar a separação em massa.");
    } finally {
      setGerandoSeparacaoMassa(false);
    }
  }

  function handleFinalizarSeparacaoConfirm() {
    mutFinalizarSeparacao.mutate({ pedidoIds: Array.from(selectedIds) });
  }

  function handleConferirConfirm() {
    const confs = usuarios
      .filter((u: any) => conferentesSelecionados.has(u.id))
      .map((u: any) => ({ id: u.id, nome: u.name }));
    mutIniciarConferencia.mutate({
      pedidoIds: Array.from(selectedIds),
      conferentes: confs,
      volumesMistos,
    });
  }

  function handleFinalizarConferenciaConfirm() {
    const pedidoId = Array.from(selectedIds)[0];
    setErroFinalizarConf(null);
    mutFinalizarConferencia.mutate({ pedidoId, volumes: volumesConferencia, pesoBruto: pesoBrutoConferencia });
  }

  function abrirDetalhesParaReimpressao(renderizar: (pedido: any, win: Window) => Promise<void> | void) {
    const pedidoId = Array.from(selectedIds)[0];
    const win = window.open("", "_blank");
    if (!win) return;

    void utils.pedidos.getDetalhes.fetch({ id: pedidoId })
      .then((pedido) => {
        if (!pedido) {
          win.close();
          return;
        }
        return renderizar(pedido, win);
      })
      .catch(() => win.close());
  }

  function handleReimprimirVia() {
    const pedidoIds = Array.from(selectedIds);
    const win = window.open("", "_blank");
    if (!win) return;

    void Promise.all(pedidoIds.map((id) => utils.pedidos.getDetalhes.fetch({ id })))
      .then((detalhes) => {
        const pedidosDetalhados = detalhes.filter(Boolean);
        if (pedidosDetalhados.length === 0) {
          win.close();
          return;
        }
        gerarPDFSeparacaoLote(pedidosDetalhados, win);
      })
      .catch(() => win.close());
  }

  function handleReimprimirEtiquetasVolume() {
    abrirDetalhesParaReimpressao((pedido, win) => gerarEtiquetasVolume(pedido, pedido.volumes, win));
  }

  function handleReimprimirEtiquetasMisto() {
    abrirDetalhesParaReimpressao((pedido, win) => gerarEtiquetasVolumeMisto(pedido, pedido.volumesMistos, win));
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="container mx-auto p-6">
      {/* ── Barra de Ações ── */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Button
          size="sm"
          variant="default"
          disabled={nSelecionados === 0 || algumSelecionadoEmRevisao || algumSelecionadoEmConferencia || algumSelecionadoSolicitarExpedicao}
          onClick={() => setModalSepararOpen(true)}
          title={algumSelecionadoEmRevisao ? "Não é possível separar: pedido em revisão selecionado" : algumSelecionadoEmConferencia ? "Não é possível separar: pedido em conferência selecionado" : "Iniciar separação dos pedidos selecionados"}
        >
          <Scissors className="h-4 w-4 mr-1" /> Separar
          {nSelecionados > 0 && <span className="ml-1 bg-white text-blue-700 rounded-full px-1.5 text-xs font-bold">{nSelecionados}</span>}
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={nSelecionados === 0 || algumSelecionadoEmConferencia || algumSelecionadoSolicitarExpedicao}
          onClick={() => setModalFinalizarSepOpen(true)}
          title={algumSelecionadoEmConferencia ? "Não é possível finalizar separação: pedido em conferência selecionado" : "Finalizar separação dos pedidos selecionados"}
        >
          <CheckSquare className="h-4 w-4 mr-1" /> Finalizar Separação
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={nSelecionados === 0 || algumSelecionadoEmRevisao || algumSelecionadoEmConferencia || algumSelecionadoSolicitarExpedicao}
          onClick={() => setModalConferirOpen(true)}
          title={algumSelecionadoEmRevisao ? "Não é possível conferir: pedido em revisão selecionado" : algumSelecionadoEmConferencia ? "Não é possível conferir: pedido em conferência selecionado" : "Iniciar conferência dos pedidos selecionados"}
        >
          <ClipboardCheck className="h-4 w-4 mr-1" /> Conferir
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={!pedidoSelecionadoEmConferencia}
          onClick={() => { setErroFinalizarConf(null); setModalFinalizarConfOpen(true); }}
          title={pedidoSelecionadoEmConferencia ? "Finalizar conferência e iniciar faturamento" : "Selecione um único pedido em conferência"}
        >
          <Check className="h-4 w-4 mr-1" /> Finalizar Conferência
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={!pedidoSelecionadoParaReposicao}
          onClick={handleAbrirReposicao}
          title={pedidoSelecionadoParaReposicao ? "Solicitar reposição de itens do pedido" : "Selecione um pedido com status 'pendente' ou 'em separação'"}
        >
          <PackageSearch className="h-4 w-4 mr-1" /> Solicitar Reposição
        </Button>
        {user?.role === 'admin' && (
          <Button
            size="sm"
            variant="outline"
            disabled={!pedidoSelecionadoRetificavel}
            onClick={handleAbrirRetificacao}
            title={pedidoSelecionadoRetificavel ? "Excluir um item ou reduzir sua quantidade" : "Selecione um único pedido pendente, em separação ou em conferencia"}
          >
            <Flag className="h-4 w-4 mr-1" /> Retificar Pedido
          </Button>
        )}
        <Button size="sm" variant="outline" disabled title="Em breve">
          <Send className="h-4 w-4 mr-1" /> Expedir
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={nSelecionados === 0}
          onClick={() => setModalSeparacaoMassaOpen(true)}
          title={nSelecionados > 0 ? "Gerar PDF ou Excel consolidado dos pedidos selecionados" : "Selecione um ou mais pedidos"}
        >
          <Package className="h-4 w-4 mr-1" /> Separação em Massa
        </Button>
        <Button size="sm" variant="outline" disabled title="Em breve">
          <BarChart2 className="h-4 w-4 mr-1" /> Relatório
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={nSelecionados === 0}
          onClick={handleReimprimirVia}
          title={nSelecionados > 0 ? "Reimprimir via de separação dos pedidos selecionados" : "Selecione um ou mais pedidos"}
        >
          <Printer className="h-4 w-4 mr-1" /> Reimprimir Via de Separação
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={!pedidoSelecionadoReimprimirEtiquetaVolume}
          onClick={handleReimprimirEtiquetasVolume}
          title={pedidoSelecionadoReimprimirEtiquetaVolume ? "Reimprimir etiquetas de volume" : "Selecione um único pedido em revisão ou solicitar expedição"}
        >
          <Tag className="h-4 w-4 mr-1" /> Reimprimir Etiquetas Volume
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={!pedidoSelecionadoRetificarVolumes}
          onClick={handleAbrirRetificarVolumes}
          title={pedidoSelecionadoRetificarVolumes ? "Retificar volumes" : "Selecione um único pedido em revisão ou solicitar expedição"}
        >
          <Tag className="h-4 w-4 mr-1" /> Retificar Volumes
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={!pedidoSelecionadoReimprimirEtiquetaMisto}
          onClick={handleReimprimirEtiquetasMisto}
          title={pedidoSelecionadoReimprimirEtiquetaMisto ? "Reimprimir etiquetas de volume misto" : "Selecione um único pedido a partir de em conferência"}
        >
          <Tag className="h-4 w-4 mr-1" /> Reimprimir Etiquetas Misto
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={!pedidoSelecionadoRetificarVolumesMistos}
          onClick={handleAbrirRetificarVolumesMistos}
          title={pedidoSelecionadoRetificarVolumesMistos ? "Retificar volumes mistos" : "Selecione um único pedido a partir de em conferência"}
        >
          <Tag className="h-4 w-4 mr-1" /> Retificar Volumes Mistos
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Pedidos em Aberto
            {nSelecionados > 0 && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                {nSelecionados} selecionado{nSelecionados > 1 ? "s" : ""}
              </span>
            )}
            <Button variant="outline" size="icon" className="relative ml-auto" title="Notificações de pedidos" onClick={() => setNotificacoesAbertas(true)}>
              <Bell className="h-4 w-4" />
              {notificacoesNaoLidas > 0 && <span className="absolute -right-1 -top-1 min-w-4 rounded-full bg-orange-500 px-1 text-[10px] leading-4 text-white">{notificacoesNaoLidas > 99 ? "99+" : notificacoesNaoLidas}</span>}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filtro */}
          <div className="mb-4">
            <div className="flex items-center gap-2 flex-wrap rounded border bg-slate-50 p-2">
              <span className="text-sm font-medium">Busca e escopo:</span>
              {filtroPrazo && <Button size="sm" variant="outline" className={filtroPrazo === "atraso" ? "border-red-300 text-red-800" : "border-green-300 text-green-800"} onClick={() => setFiltroPrazo(null)}>{filtroPrazo === "atraso" ? "Em atraso" : "No prazo"}<X className="ml-1 h-3 w-3" /></Button>}
              <Input
                placeholder="Buscar por número, referência da importação, destinatário, status, criado por..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-md"
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline">
                    {emitenteIdsAplicados.size === 0 || emitenteIdsAplicados.size === emitentesDisponiveis.length
                      ? "Todos os emitentes"
                      : `Emitentes (${emitenteIdsAplicados.size})`}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-72 p-2">
                  <label className="flex items-center gap-2 cursor-pointer rounded px-2 py-1.5 hover:bg-gray-50">
                    <Checkbox
                      checked={todosEmitentesSelecionados}
                      onCheckedChange={toggleTodosEmitentes}
                    />
                    <span className="text-sm font-medium">Selecionar todos</span>
                  </label>
                  <div className="my-2 max-h-56 overflow-y-auto border-y py-1">
                    {emitentesDisponiveis.map((emitente) => (
                      <label key={emitente.id} className="flex items-center gap-2 cursor-pointer rounded px-2 py-1.5 hover:bg-gray-50">
                        <Checkbox
                          checked={emitenteIdsTemporarios.has(emitente.id)}
                          onCheckedChange={() => toggleEmitenteTemporario(emitente.id)}
                        />
                        <span className="text-sm">{emitente.nome}</span>
                      </label>
                    ))}
                  </div>
                  <Button size="sm" className="w-full" onClick={aplicarFiltroEmitentes}>
                    Aplicar
                  </Button>
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline">
                    {vendedorIdsAplicados.size === 0 || vendedorIdsAplicados.size === vendedoresDisponiveis.length
                      ? "Todos os vendedores"
                      : `Vendedores (${vendedorIdsAplicados.size})`}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-72 p-2">
                  <label className="flex items-center gap-2 cursor-pointer rounded px-2 py-1.5 hover:bg-gray-50">
                    <Checkbox
                      checked={todosVendedoresSelecionados}
                      onCheckedChange={toggleTodosVendedores}
                    />
                    <span className="text-sm font-medium">Selecionar todos</span>
                  </label>
                  <div className="my-2 max-h-56 overflow-y-auto border-y py-1">
                    {vendedoresDisponiveis.map((vendedor) => (
                      <label key={vendedor.id} className="flex items-center gap-2 cursor-pointer rounded px-2 py-1.5 hover:bg-gray-50">
                        <Checkbox
                          checked={vendedorIdsTemporarios.has(vendedor.id)}
                          onCheckedChange={() => toggleVendedorTemporario(vendedor.id)}
                        />
                        <span className="text-sm">{vendedor.nome}</span>
                      </label>
                    ))}
                  </div>
                  <Button size="sm" className="w-full" onClick={aplicarFiltroVendedores}>
                    Aplicar
                  </Button>
                </DropdownMenuContent>
              </DropdownMenu>
              <span className="text-sm font-medium">De:</span>
              <Input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} className="w-auto" />
              <span className="text-sm font-medium">Até:</span>
              <Input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} className="w-auto" />
            </div>
            <div className="flex items-center gap-2 flex-wrap mt-2 rounded border bg-slate-50 p-2">
              <span className="text-sm font-medium">Pedido — Status:</span>
              <Button
                size="sm"
                variant={statusFilters.size === 0 ? "default" : "outline"}
                onClick={() => setStatusFilters(new Set())}
              >
                Todos
              </Button>
              {statusDisponiveis.map((status) => (
                <Button
                  key={status}
                  size="sm"
                  variant={statusFilters.has(status) ? "default" : "outline"}
                  onClick={() => toggleStatusFilter(status)}
                >
                  {status}
                </Button>
              ))}
            </div>
            <div className="flex items-center gap-2 flex-wrap mt-2 rounded border bg-slate-50 p-2">
              <span className="text-sm font-medium">Pedido — Tipo de Entrega:</span>
              <Button
                size="sm"
                variant={tipoEntregaFilters.size === 0 ? "default" : "outline"}
                onClick={() => setTipoEntregaFilters(new Set())}
              >
                Todos
              </Button>
              {tiposEntregaDisponiveis.map((tipoEntrega) => (
                <Button
                  key={tipoEntrega}
                  size="sm"
                  variant={tipoEntregaFilters.has(tipoEntrega) ? "default" : "outline"}
                  onClick={() => toggleTipoEntregaFilter(tipoEntrega)}
                >
                  {tipoEntrega}
                </Button>
              ))}
            </div>
            <div className="flex items-center gap-2 flex-wrap mt-2 rounded border bg-slate-50 p-2">
              <span className="text-sm font-medium">Pedido — Entre emitentes:</span>
              <Button size="sm" variant={!filtroEntreEmitentes ? "default" : "outline"} onClick={() => setFiltroEntreEmitentes(false)}>Todos</Button>
              <Button size="sm" variant={filtroEntreEmitentes ? "default" : "outline"} onClick={() => setFiltroEntreEmitentes(true)}>Entre emitente</Button>
            </div>
            <div className="flex items-center gap-2 flex-wrap mt-2 rounded border border-green-200 bg-green-50 p-2">
              <span className="text-sm font-medium text-green-950">GNRE:</span>
              <Button size="sm" variant={gnreStatusFilter === "todos" ? "default" : "outline"} onClick={() => setGnreStatusFilter("todos")}>Todos</Button>
              <Button size="sm" variant={gnreStatusFilter === "sem_gnre" ? "default" : "outline"} onClick={() => setGnreStatusFilter("sem_gnre")}>Sem GNRE</Button>
              <Button size="sm" variant={gnreStatusFilter === "pendente" ? "default" : "outline"} onClick={() => setGnreStatusFilter("pendente")}>Pendente</Button>
              <Button size="sm" variant={gnreStatusFilter === "processado" ? "default" : "outline"} onClick={() => setGnreStatusFilter("processado")}>Processado</Button>
              <Button size="sm" variant={gnreStatusFilter === "processado_pago" ? "default" : "outline"} onClick={() => setGnreStatusFilter("processado_pago")}>Processado - Pago</Button>
              <Button size="sm" variant={gnreStatusFilter === "erro" ? "default" : "outline"} onClick={() => setGnreStatusFilter("erro")}>Erro</Button>
            </div>
          </div>

          {isLoading && <p className="text-center text-muted-foreground">Carregando...</p>}

          {!isLoading && filteredPedidos.length === 0 && (
            <p className="text-center text-muted-foreground">
              {searchTerm || statusFilters.size > 0 || tipoEntregaFilters.size > 0 || gnreStatusFilter !== "todos" || filtroEntreEmitentes || emitenteIdsAplicados.size > 0 || vendedorIdsAplicados.size > 0 || dataInicio || dataFim
                ? "Nenhum pedido encontrado"
                : "Nenhum pedido em aberto"}
            </p>
          )}

          {!isLoading && filteredPedidos.length > 0 && (
            <>
              <div className="relative w-full max-w-full min-w-0 overflow-x-auto overflow-y-hidden pb-2 [scrollbar-color:#f97316_#e5e7eb] [scrollbar-width:auto] [&::-webkit-scrollbar]:h-3 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-orange-500 [&::-webkit-scrollbar-thumb:hover]:bg-orange-600">
                <ReorderableTable
                  pageKey="open-orders"
                  userId={user?.id}
                  columns={pedidoColumns}
                  rows={paginatedPedidos}
                  getRowKey={(pedido) => pedido.id}
                  getRowProps={(pedido) => ({ className: selectedIds.has(pedido.id) ? "bg-blue-50" : "" })}
                  sort={{ column: sortColumn, direction: sortDirection, onToggle: toggleSort }}
                  tableClassName="w-full text-sm"
                />
              </div>

              {/* Paginação */}
              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Página {currentPage} de {totalPages} ({filteredPedidos.length} pedidos)
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>Anterior</Button>
                    <Button variant="outline" size="sm" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>Próxima</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ══════════════════════════════════════════════════════════════════════
          MODAL — Detalhes do Pedido (olhinho)
      ══════════════════════════════════════════════════════════════════════ */}
      <Dialog open={detalhesPedidoId !== null} onOpenChange={(o) => { if (!o) setDetalhesPedidoId(null); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" style={{ width: '80vw', maxWidth: '80vw' }}>
          <DialogHeader>
            <DialogTitle>
              Detalhes do Pedido {pedidoDetalhes ? `#${pedidoDetalhes.id}` : ""}
            </DialogTitle>
          </DialogHeader>

          {loadingDetalhes && <p className="text-center text-muted-foreground py-8">Carregando...</p>}

          {pedidoDetalhes && !loadingDetalhes && (
            <div className="space-y-6">
              {/* Emitente */}
              <section>
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-2">Emitente</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="font-medium">Razão Social:</span> {pedidoDetalhes.emitenteNome || "-"}</div>
                  <div><span className="font-medium">Nome Fantasia:</span> {pedidoDetalhes.emitenteFantasia || "-"}</div>
                  <div><span className="font-medium">CNPJ:</span> {pedidoDetalhes.emitenteCnpj || "-"}</div>
                  <div><span className="font-medium">UF:</span> {pedidoDetalhes.emitenteUf || "-"}</div>
                </div>
              </section>

              {/* Destinatário */}
              <section>
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-2">Destinatário</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="font-medium">Razão Social:</span> {pedidoDetalhes.destinatarioRazao || "-"}</div>
                  <div><span className="font-medium">Nome Fantasia:</span> {pedidoDetalhes.destinatarioFantasia || "-"}</div>
                  <div><span className="font-medium">CNPJ:</span> {pedidoDetalhes.destinatarioCnpj || "-"}</div>
                  <div><span className="font-medium">Cidade/UF:</span> {pedidoDetalhes.destinatarioCidade || "-"} / {pedidoDetalhes.destinatarioUf || "-"}</div>
                  <div className="col-span-2">
                    <span className="font-medium">Endereço:</span>{" "}
                    {[pedidoDetalhes.destinatarioLogradouro, pedidoDetalhes.destinatarioNumero, pedidoDetalhes.destinatarioBairro, pedidoDetalhes.destinatarioCep].filter(Boolean).join(", ") || "-"}
                  </div>
                </div>
              </section>

              {/* Configuração do Orçamento */}
              <section>
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-2">Configuração do Orçamento</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="font-medium">Tipo de Orçamento:</span> {pedidoDetalhes.tipoOrcamentoNome || "-"}</div>
                  <div><span className="font-medium">Condição de Pagamento:</span> {pedidoDetalhes.condicaoPagamentoDesc || "-"}</div>
                  <div><span className="font-medium">Tipo de Frete:</span> {pedidoDetalhes.tipoFrete || "-"}</div>
                  <div><span className="font-medium">Tipo de Entrega:</span> {pedidoDetalhes.tipoEntrega || "-"}</div>
                  <div><span className="font-medium">Transportadora:</span> {pedidoDetalhes.transportadora || "-"}</div>
                  <div><span className="font-medium">Tag:</span> {pedidoDetalhes.tag || pedidoDetalhes.orcamentoTag || "-"}</div>
                  <div><span className="font-medium">Status:</span> <StatusBadge status={pedidoDetalhes.status} /></div>
                  <div><span className="font-medium">Data:</span> {formatDate(pedidoDetalhes.criadoEm)}</div>
                </div>
              </section>

              <section>
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-2">Etiquetas</h3>
                <EtiquetasEditor modulo="pedido" entidadeId={pedidoDetalhes.id} />
              </section>

              {/* Observações */}
              {(pedidoDetalhes.obsPedidoInterno || pedidoDetalhes.obsPedidoDestinatario || pedidoDetalhes.orcamentoAnotacoes || pedidoDetalhes.anotacoes) && (
                <section>
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-2">Observações</h3>
                  <div className="space-y-2 text-sm">
                    {pedidoDetalhes.obsPedidoInterno && (
                      <div>
                        <span className="font-medium">Obs. Interno:</span>
                        <p className="mt-1 p-2 bg-gray-50 rounded text-xs whitespace-pre-wrap">{pedidoDetalhes.obsPedidoInterno}</p>
                      </div>
                    )}
                    {pedidoDetalhes.obsPedidoDestinatario && (
                      <div>
                        <span className="font-medium">Obs. Destinatário:</span>
                        <p className="mt-1 p-2 bg-gray-50 rounded text-xs whitespace-pre-wrap">{pedidoDetalhes.obsPedidoDestinatario}</p>
                      </div>
                    )}
                    {pedidoDetalhes.orcamentoAnotacoes && (
                      <div>
                        <span className="font-medium">Anotações do Orçamento:</span>
                        <p className="mt-1 p-2 bg-gray-50 rounded text-xs whitespace-pre-wrap">{pedidoDetalhes.orcamentoAnotacoes}</p>
                      </div>
                    )}
                    {pedidoDetalhes.anotacoes && (
                      <div>
                        <span className="font-medium">Anotações do Pedido:</span>
                        <p className="mt-1 p-2 bg-gray-50 rounded text-xs whitespace-pre-wrap">{pedidoDetalhes.anotacoes}</p>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {/* Itens */}
              <section>
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-2">
                  Itens do Pedido ({(pedidoDetalhes.itens || []).length})
                </h3>
                {(pedidoDetalhes.itens || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nenhum item cadastrado.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="border px-3 py-2 text-left">Código</th>
                          <th className="border px-3 py-2 text-left">Descrição</th>
                          <th className="border px-3 py-2 text-right">Qtd.</th>
                          <th className="border px-3 py-2 text-right">Cx. Master</th>
                          <th className="border px-3 py-2 text-right">Vl. Unit.</th>
                          <th className="border px-3 py-2 text-right">Total</th>
                          <th className="border px-3 py-2 text-left">Endereços</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(pedidoDetalhes.itens || []).map((item: any) => (
                          <tr key={item.id} className="hover:bg-gray-50">
                            <td className="border px-3 py-2 font-mono text-xs">{item.productCod}</td>
                            <td className="border px-3 py-2">{item.productDescription}</td>
                            <td className="border px-3 py-2 text-right">{parseFloat(item.quantidade).toFixed(0)}</td>
                            <td className="border px-3 py-2 text-right">{item.qtdCaixaMaster || "-"}</td>
                            <td className="border px-3 py-2 text-right">R$ {parseFloat(item.valorUnitario).toFixed(2)}</td>
                            <td className="border px-3 py-2 text-right">R$ {parseFloat(item.valorTotal).toFixed(2)}</td>
                            <td className="border px-3 py-2 text-xs text-muted-foreground">
                              {(item.enderecos || []).length > 0 ? item.enderecos.join(", ") : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetalhesPedidoId(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={anotacoesModalOpen} onOpenChange={(o) => { if (!o) fecharAnotacoesPedido(); }}>
        <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Anotações — Pedido #{anotacoesPedidoId}</DialogTitle>
          </DialogHeader>
          {anotacoesDetalhes && (anotacoesDetalhes.orcamentoAnotacoes || anotacoesDetalhes.obsPedidoInterno || anotacoesDetalhes.obsPedidoDestinatario) && (
            <div className="mb-4 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Obs do Orçamento</p>
              {anotacoesDetalhes.orcamentoAnotacoes && <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm"><p className="text-xs font-medium text-yellow-700 mb-1">Anotações</p><p className="whitespace-pre-wrap">{anotacoesDetalhes.orcamentoAnotacoes}</p></div>}
              {anotacoesDetalhes.obsPedidoInterno && <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm"><p className="text-xs font-medium text-yellow-700 mb-1">Obs Interno</p><p className="whitespace-pre-wrap">{anotacoesDetalhes.obsPedidoInterno}</p></div>}
              {anotacoesDetalhes.obsPedidoDestinatario && <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm"><p className="text-xs font-medium text-yellow-700 mb-1">Obs Destinatário</p><p className="whitespace-pre-wrap">{anotacoesDetalhes.obsPedidoDestinatario}</p></div>}
            </div>
          )}
          {anotacoesDetalhes?.anotacoes && (
            <div className="mb-4 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Histórico do Pedido</p>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm"><p className="whitespace-pre-wrap">{anotacoesDetalhes.anotacoes}</p></div>
            </div>
          )}
          <div className="space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Anotações do Pedido</p>
            {anotacoesList.length === 0 && <p className="text-sm text-muted-foreground">Nenhuma anotação ainda.</p>}
            {anotacoesList.map((anotacao: any) => (
              <div key={anotacao.id} className="p-3 bg-gray-50 rounded text-sm">
                <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>{anotacao.autorNome}</span><span>{new Date(anotacao.criadoEm).toLocaleString("pt-BR")}</span></div>
                <p className="whitespace-pre-wrap">{anotacao.texto}</p>
                {(anotacao.mencoes ?? []).length > 0 && <p className="mt-2 text-xs text-muted-foreground">Mencionados: {(anotacao.mencoes ?? []).map((mencao: any) => `@${mencao.usuarioNome || "usuário"}`).join(", ")}</p>}
                {(anotacao.arquivos ?? []).length > 0 && <div className="mt-3 flex flex-wrap gap-2">{(anotacao.arquivos ?? []).map((arquivo: any) => <a key={arquivo.id} href={arquivo.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 rounded border bg-white px-2 py-1 text-xs hover:bg-slate-50"><Paperclip className="h-3 w-3" />{arquivo.nome}{arquivo.categoria === "comprovante_gnre" && <span className="rounded bg-green-950 px-1.5 py-0.5 text-[10px] font-medium text-white">Comprovante de GNRE</span>}</a>)}</div>}
              </div>
            ))}
          </div>
          <div className="mt-4 rounded border p-3 space-y-2">
            <Textarea
              placeholder="Nova anotação... Use @ para marcar uma pessoa"
              value={novaAnotacao}
              onChange={(e) => { setNovaAnotacao(e.target.value); setMenuMencaoAberto(obterSugestoesMencao(e.target.value).length > 0); }}
              onPaste={(e) => { const arquivos = Array.from(e.clipboardData.files); if (arquivos.length) adicionarArquivosAnotacao(arquivos); }}
              rows={3}
            />
            {menuMencaoAberto && obterSugestoesMencao(novaAnotacao).length > 0 && <div className="rounded border bg-white p-1 shadow-sm">{obterSugestoesMencao(novaAnotacao).map((participante: any) => <button key={participante.id} type="button" className="block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-slate-100" onClick={() => { setNovaAnotacao((texto) => inserirMencaoNoTexto(texto, participante.name)); setMencoesSelecionadas((ids) => ids.includes(participante.id) ? ids : [...ids, participante.id]); setMenuMencaoAberto(false); }}>{participante.name || "Usuário"}{participante.contextoMencao ? ` - ${participante.contextoMencao}` : ""}</button>)}</div>}
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => anotacaoFileInputRef.current?.click()}><Paperclip className="mr-1 h-3.5 w-3.5" />Anexar</Button>
              <input ref={anotacaoFileInputRef} type="file" multiple className="hidden" onChange={(e) => { adicionarArquivosAnotacao(Array.from(e.target.files ?? [])); e.target.value = ""; }} />
              <Button type="button" variant="outline" size="sm" className="border-green-900 text-green-950 hover:bg-green-50" onClick={() => comprovanteGnreFileInputRef.current?.click()}><Paperclip className="mr-1 h-3.5 w-3.5" />Comprovante de GNRE</Button>
              <input ref={comprovanteGnreFileInputRef} type="file" multiple className="hidden" onChange={(e) => { adicionarArquivosAnotacao(Array.from(e.target.files ?? []), "comprovante_gnre"); e.target.value = ""; }} />
              {progressoAnotacao && <span className="text-xs text-muted-foreground">Enviando {progressoAnotacao.atual}/{progressoAnotacao.total}</span>}
              <Button className="ml-auto" disabled={(!novaAnotacao.trim() && !anotacaoArquivos.length && !comprovantesGnre.length) || isEnviandoAnotacao} onClick={handleEnviarAnotacao}>{isEnviandoAnotacao ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Enviando...</> : <><Send className="h-4 w-4 mr-2" />Adicionar Anotação</>}</Button>
            </div>
            {anotacaoArquivos.length > 0 && <div className="flex flex-wrap gap-2">{anotacaoArquivos.map((arquivo, indice) => <button key={`${arquivo.name}-${indice}`} type="button" className="rounded border bg-slate-50 px-2 py-1 text-xs" onClick={() => setAnotacaoArquivos((atuais) => atuais.filter((_, index) => index !== indice))}>{arquivo.name} <X className="inline h-3 w-3" /></button>)}</div>}
            {comprovantesGnre.length > 0 && <div className="flex flex-wrap gap-2">{comprovantesGnre.map((arquivo, indice) => <button key={`${arquivo.name}-${indice}`} type="button" className="rounded border border-green-900 bg-green-50 px-2 py-1 text-xs text-green-950" onClick={() => setComprovantesGnre((atuais) => atuais.filter((_, index) => index !== indice))}>Comprovante de GNRE: {arquivo.name} <X className="inline h-3 w-3" /></button>)}</div>}
          </div>
          <DialogFooter className="mt-2"><Button variant="outline" onClick={fecharAnotacoesPedido}>Fechar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={notificacoesAbertas} onOpenChange={setNotificacoesAbertas}>
        <DialogContent className="max-h-[80vh] max-w-lg overflow-y-auto">
          <DialogHeader><DialogTitle>Notificações de Pedidos</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {notificacoes.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">Nenhuma notificação.</p> : notificacoes.map((notificacao: any) => (
              <button key={notificacao.id} className={`w-full rounded-lg border p-3 text-left text-sm ${notificacao.lida ? "bg-white" : "border-orange-200 bg-orange-50"}`} onClick={() => { marcarNotificacaoLidaMutation.mutate({ id: notificacao.id }); setNotificacoesAbertas(false); abrirAnotacoesPedido(Number(notificacao.pedidoId)); }}>
                <strong className="block truncate">{notificacao.titulo}</strong>
                {notificacao.mensagem && <span className="mt-1 block text-xs text-muted-foreground">{notificacao.mensagem}</span>}
                <small className="mt-1 block text-xs text-muted-foreground">{new Date(notificacao.criadoEm).toLocaleString("pt-BR")}</small>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════════════
          MODAL — Retificar Pedido
      ══════════════════════════════════════════════════════════════════════ */}
      <Dialog open={modalRetificarOpen} onOpenChange={(open) => {
        if (!open && !mutRetificarItem.isPending) {
          setModalRetificarOpen(false);
          setRetificarPedidoId(null);
          setRetificarItemId("");
          setRetificarQuantidade("");
          setRetificarMotivo("");
          setRetificarConfirmacaoFisica(false);
        }
      }}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Retificar Pedido {pedidoRetificacaoDetalhes ? `#${pedidoRetificacaoDetalhes.id}` : ""}</DialogTitle>
          </DialogHeader>

          {loadingRetificacaoDetalhes && <p className="text-center text-muted-foreground py-8">Carregando itens...</p>}

          {pedidoRetificacaoDetalhes && !loadingRetificacaoDetalhes && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                A retificação devolve somente a quantidade removida ao estoque global e, quando houver movimentação por endereço, aos mesmos endereços de origem. O status do pedido não será alterado.
              </p>

              <div className="space-y-1">
                <label className="text-sm font-medium">Item</label>
                <Select value={retificarItemId} onValueChange={(value) => {
                  setRetificarItemId(value);
                  const item = (pedidoRetificacaoDetalhes.itens || []).find((registro: any) => String(registro.id) === value);
                  setRetificarQuantidade(item ? String(item.quantidade) : "");
                }} disabled={mutRetificarItem.isPending}>
                    <SelectTrigger className="w-full min-w-0 overflow-hidden"><SelectValue className="truncate" placeholder="Selecione o item" /></SelectTrigger>
                  <SelectContent>
                    {(pedidoRetificacaoDetalhes.itens || []).map((item: any) => (
                      <SelectItem key={item.id} value={String(item.id)}>
                        {item.productCod} — {item.productDescription} — Qtd. {item.quantidade}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Operação</label>
                  <Select value={retificarModo} onValueChange={(value) => setRetificarModo(value as 'excluir' | 'reduzir')} disabled={mutRetificarItem.isPending}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="reduzir">Reduzir quantidade</SelectItem>
                      <SelectItem value="excluir">Excluir item</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {retificarModo === 'reduzir' && (
                  <div className="space-y-1">
                    <label className="text-sm font-medium">Nova quantidade</label>
                    <Input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={retificarQuantidade}
                      onChange={(event) => setRetificarQuantidade(event.target.value)}
                      disabled={mutRetificarItem.isPending || !retificarItemId}
                    />
                  </div>
                )}
              </div>

              {retificarItemId && (() => {
                const item = (pedidoRetificacaoDetalhes.itens || []).find((registro: any) => String(registro.id) === retificarItemId);
                const quantidadeAtual = Number(item?.quantidade ?? 0);
                const quantidadeFinal = retificarModo === 'excluir' ? 0 : Number(retificarQuantidade);
                const quantidadeRemovida = Number.isFinite(quantidadeFinal) ? Math.max(0, quantidadeAtual - quantidadeFinal) : 0;
                return (
                  <div className="rounded border bg-amber-50 p-3 text-sm">
                    <div><span className="font-medium">Quantidade atual:</span> {quantidadeAtual}</div>
                    <div><span className="font-medium">Quantidade final:</span> {retificarModo === 'excluir' ? 0 : retificarQuantidade || '-'}</div>
                    <div><span className="font-medium">Quantidade devolvida ao estoque:</span> {quantidadeRemovida}</div>
                  </div>
                );
              })()}

              <div className="space-y-1">
                <label className="text-sm font-medium">Motivo</label>
                <textarea
                  value={retificarMotivo}
                  onChange={(event) => setRetificarMotivo(event.target.value)}
                  disabled={mutRetificarItem.isPending}
                  className="w-full min-h-20 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  placeholder="Informe o motivo da retificação"
                />
              </div>

              {pedidoRetificacaoDetalhes.status !== 'pendente' && (
                <label className="flex items-start gap-2 rounded border p-3 text-sm">
                  <Checkbox
                    checked={retificarConfirmacaoFisica}
                    onCheckedChange={(checked) => setRetificarConfirmacaoFisica(checked === true)}
                    disabled={mutRetificarItem.isPending}
                  />
                  <span>Confirmo que a quantidade removida será devolvida fisicamente ao estoque usado pelo pedido.</span>
                </label>
              )}
            </div>
          )}

          <DialogFooter className="flex-col-reverse gap-2 sm:flex-row">
            <Button className="w-full sm:w-auto" variant="outline" onClick={() => setModalRetificarOpen(false)} disabled={mutRetificarItem.isPending}>Cancelar</Button>
            <Button className="w-full sm:w-auto" onClick={handleConfirmarRetificacao} disabled={mutRetificarItem.isPending || !pedidoRetificacaoDetalhes}>
              {mutRetificarItem.isPending ? "Retificando..." : "Confirmar Retificação"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={modalRetificarVolumesOpen} onOpenChange={(open) => {
        if (!open && !mutRetificarVolumes.isPending) {
          setModalRetificarVolumesOpen(false);
          setVolumesRetificacao(1);
        }
      }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Retificar Volumes</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Quantidade de volumes</label>
              <Input
                type="number"
                min={1}
                value={volumesRetificacao}
                onChange={(event) => setVolumesRetificacao(Math.max(1, parseInt(event.target.value) || 1))}
                disabled={mutRetificarVolumes.isPending}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalRetificarVolumesOpen(false)} disabled={mutRetificarVolumes.isPending}>Cancelar</Button>
            <Button onClick={handleConfirmarRetificarVolumes} disabled={mutRetificarVolumes.isPending}>
              {mutRetificarVolumes.isPending ? "Retificando..." : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={modalRetificarVolumesMistosOpen} onOpenChange={(open) => {
        if (!open && !mutRetificarVolumesMistos.isPending) {
          setModalRetificarVolumesMistosOpen(false);
          setVolumesMistosRetificacao(1);
        }
      }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Retificar Volumes Mistos</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Quantidade de volumes mistos</label>
              <Input
                type="number"
                min={1}
                value={volumesMistosRetificacao}
                onChange={(event) => setVolumesMistosRetificacao(Math.max(1, parseInt(event.target.value) || 1))}
                disabled={mutRetificarVolumesMistos.isPending}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalRetificarVolumesMistosOpen(false)} disabled={mutRetificarVolumesMistos.isPending}>Cancelar</Button>
            <Button onClick={handleConfirmarRetificarVolumesMistos} disabled={mutRetificarVolumesMistos.isPending}>
              {mutRetificarVolumesMistos.isPending ? "Retificando..." : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={modalSeparacaoMassaOpen} onOpenChange={(open) => !gerandoSeparacaoMassa && setModalSeparacaoMassaOpen(open)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Separação em Massa</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Gerar a lista consolidada de {nSelecionados} pedido{nSelecionados > 1 ? "s" : ""} selecionado{nSelecionados > 1 ? "s" : ""}, agrupada por código e sem separar emitentes.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalSeparacaoMassaOpen(false)} disabled={gerandoSeparacaoMassa}>Cancelar</Button>
            <Button variant="outline" onClick={() => handleGerarSeparacaoMassa("excel")} disabled={gerandoSeparacaoMassa}>
              {gerandoSeparacaoMassa ? "Gerando..." : "Excel"}
            </Button>
            <Button onClick={() => handleGerarSeparacaoMassa("pdf")} disabled={gerandoSeparacaoMassa}>
              {gerandoSeparacaoMassa ? "Gerando..." : "PDF"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════════════
          MODAL — Separar
      ══════════════════════════════════════════════════════════════════════ */}
      <Dialog open={modalSepararOpen} onOpenChange={(o) => { setModalSepararOpen(o); if (!o) { setBuscaSeparadores(""); setPaginaSeparadores(1); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Iniciar Separação — {nSelecionados} pedido{nSelecionados > 1 ? "s" : ""}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Ponto Operacional</label>
              <Select
                value={pontoOperacionalSelecionado ? String(pontoOperacionalSelecionado) : ""}
                onValueChange={(v) => setPontoOperacionalSelecionado(Number(v))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o PO..." />
                </SelectTrigger>
                <SelectContent>
                  {(pontosOperacionais ?? []).map((po: any) => (
                    <SelectItem key={po.id} value={String(po.id)}>
                      PO-{po.id} — {po.tipo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm text-muted-foreground">
              Selecione um ou mais separadores:
              {separadoresSelecionados.size > 0 && (
                <span className="ml-2 text-blue-600 font-medium">{separadoresSelecionados.size} selecionado{separadoresSelecionados.size > 1 ? "s" : ""}</span>
              )}
            </p>
            <Input
              placeholder="Buscar usuário..."
              value={buscaSeparadores}
              onChange={(e) => { setBuscaSeparadores(e.target.value); setPaginaSeparadores(1); }}
              className="h-8 text-sm"
            />
            <div className="space-y-1 border rounded p-2 min-h-[200px]">
              {usuariosPaginadosSep.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">Nenhum usuário encontrado</p>
              )}
              {usuariosPaginadosSep.map((u: any) => (
                <label key={u.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                  <Checkbox
                    checked={separadoresSelecionados.has(u.id)}
                    onCheckedChange={(checked) => {
                      const next = new Set(separadoresSelecionados);
                      if (checked) next.add(u.id); else next.delete(u.id);
                      setSeparadoresSelecionados(next);
                    }}
                  />
                  <span className="text-sm">{u.name}</span>
                </label>
              ))}
            </div>
            {totalPaginasSep > 1 && (
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Pág. {paginaSeparadores}/{totalPaginasSep} ({usuariosFiltradosSep.length} usuários)</span>
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" className="h-6 px-2 text-xs" onClick={() => setPaginaSeparadores(p => p - 1)} disabled={paginaSeparadores === 1}>&#8249;</Button>
                  <Button variant="outline" size="sm" className="h-6 px-2 text-xs" onClick={() => setPaginaSeparadores(p => p + 1)} disabled={paginaSeparadores === totalPaginasSep}>&#8250;</Button>
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Será gerado um PDF de romaneio de separação para cada pedido selecionado e o status mudará para <strong>em separação</strong>.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalSepararOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleSepararConfirm}
              disabled={separadoresSelecionados.size === 0 || !pontoOperacionalSelecionado || mutIniciarSeparacao.isPending}
            >
              {mutIniciarSeparacao.isPending ? "Salvando..." : "Confirmar Separação"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════════════
          MODAL — Finalizar Separação
      ══════════════════════════════════════════════════════════════════════ */}
      <Dialog open={modalFinalizarSepOpen} onOpenChange={setModalFinalizarSepOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Finalizar Separação</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Confirma a finalização da separação de <strong>{nSelecionados}</strong> pedido{nSelecionados > 1 ? "s" : ""}?
            O status mudará para <strong>para conferencia</strong>.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalFinalizarSepOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleFinalizarSeparacaoConfirm}
              disabled={mutFinalizarSeparacao.isPending}
            >
              {mutFinalizarSeparacao.isPending ? "Salvando..." : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════════════
          MODAL — Conferir
      ══════════════════════════════════════════════════════════════════════ */}
      <Dialog open={modalConferirOpen} onOpenChange={(o) => { setModalConferirOpen(o); if (!o) { setBuscaConferentes(""); setPaginaConferentes(1); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Iniciar Conferência — {nSelecionados} pedido{nSelecionados > 1 ? "s" : ""}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium mb-1">
                Selecione os conferentes:
                {conferentesSelecionados.size > 0 && (
                  <span className="ml-2 text-blue-600 font-medium text-xs">{conferentesSelecionados.size} selecionado{conferentesSelecionados.size > 1 ? "s" : ""}</span>
                )}
              </p>
              <Input
                placeholder="Buscar usuário..."
                value={buscaConferentes}
                onChange={(e) => { setBuscaConferentes(e.target.value); setPaginaConferentes(1); }}
                className="h-8 text-sm mb-2"
              />
              <div className="space-y-1 border rounded p-2 min-h-[180px]">
                {usuariosPaginadosConf.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">Nenhum usuário encontrado</p>
                )}
                {usuariosPaginadosConf.map((u: any) => (
                  <label key={u.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                    <Checkbox
                      checked={conferentesSelecionados.has(u.id)}
                      onCheckedChange={(checked) => {
                        const next = new Set(conferentesSelecionados);
                        if (checked) next.add(u.id); else next.delete(u.id);
                        setConferentesSelecionados(next);
                      }}
                    />
                    <span className="text-sm">{u.name}</span>
                  </label>
                ))}
              </div>
              {totalPaginasConf > 1 && (
                <div className="flex items-center justify-between text-xs text-muted-foreground mt-1">
                  <span>Pág. {paginaConferentes}/{totalPaginasConf} ({usuariosFiltradosConf.length} usuários)</span>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" className="h-6 px-2 text-xs" onClick={() => setPaginaConferentes(p => p - 1)} disabled={paginaConferentes === 1}>&#8249;</Button>
                    <Button variant="outline" size="sm" className="h-6 px-2 text-xs" onClick={() => setPaginaConferentes(p => p + 1)} disabled={paginaConferentes === totalPaginasConf}>&#8250;</Button>
                  </div>
                </div>
              )}
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Quantidade de volumes mistos:</label>
              <Input
                type="number"
                min={1}
                value={volumesMistos}
                onChange={(e) => setVolumesMistos(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-32"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Serão geradas <strong>{volumesMistos}</strong> etiqueta{volumesMistos > 1 ? "s" : ""} de volume misto (10×15 cm) com QR Code único por volume. O status mudará para <strong>em conferencia</strong>.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalConferirOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleConferirConfirm}
              disabled={conferentesSelecionados.size === 0 || mutIniciarConferencia.isPending}
            >
              {mutIniciarConferencia.isPending ? "Salvando..." : "Confirmar e Gerar Etiquetas"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════════════
          MODAL — Finalizar Conferência
      ══════════════════════════════════════════════════════════════════════ */}
      <Dialog open={modalFinalizarConfOpen} onOpenChange={(o) => {
        if (!o && !mutFinalizarConferencia.isPending) {
          setModalFinalizarConfOpen(false);
          setErroFinalizarConf(null);
          setPesoBrutoConferencia(0);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Finalizar Conferência</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Alerta de erro */}
            {erroFinalizarConf && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Erro no processo de faturamento</p>
                  <p className="mt-1 text-xs">{erroFinalizarConf}</p>
                  <p className="mt-1 text-xs">O pedido foi movido para <strong>em revisão</strong>.</p>
                </div>
              </div>
            )}

            {!erroFinalizarConf && (
              <>
                <div className="flex gap-4">
                  <div>
                    <label className="text-sm font-medium block mb-1">Quantidade de volumes:</label>
                    <Input
                      type="number"
                      min={1}
                      value={volumesConferencia}
                      onChange={(e) => setVolumesConferencia(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-32"
                      disabled={mutFinalizarConferencia.isPending}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">Peso bruto (kg):</label>
                    <Input
                      type="number"
                      min={0}
                      step={0.001}
                      value={pesoBrutoConferencia}
                      onChange={(e) => setPesoBrutoConferencia(Math.max(0, parseFloat(e.target.value) || 0))}
                      className="w-32"
                      disabled={mutFinalizarConferencia.isPending}
                    />
                  </div>
                </div>

                <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800 space-y-1">
                  <p className="font-medium">Ao confirmar, o sistema irá:</p>
                  <ol className="list-decimal list-inside text-xs space-y-0.5 mt-1">
                    <li>Gerar e abrir as etiquetas de volume ({volumesConferencia} etiqueta{volumesConferencia > 1 ? "s" : ""} 10×15 cm)</li>
                    <li>Gerar o XML da NF-e e transmitir à SEFAZ</li>
                    <li>Emitir GNRE (se aplicável)</li>
                    <li>Gerar boleto(s) conforme condição de pagamento</li>
                    <li>Lançar no contas a receber</li>
                    <li>Disparar e-mail de cobrança ao destinatário</li>
                    <li>Mover o pedido para <strong>solicitar expedição</strong></li>
                  </ol>
                  <p className="text-xs mt-2 text-blue-600">Em caso de erro em qualquer etapa, o pedido será movido para <strong>em revisão</strong>.</p>
                </div>
              </>
            )}

            {mutFinalizarConferencia.isPending && (
              <div className="flex items-center gap-2 p-3 bg-gray-50 border rounded text-sm text-gray-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                <span>Processando faturamento... Aguarde.</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setModalFinalizarConfOpen(false); setErroFinalizarConf(null); }}
              disabled={mutFinalizarConferencia.isPending}
            >
              {erroFinalizarConf ? "Fechar" : "Cancelar"}
            </Button>
            {!erroFinalizarConf && (
              <Button
                onClick={handleFinalizarConferenciaConfirm}
                disabled={mutFinalizarConferencia.isPending}
              >
                {mutFinalizarConferencia.isPending ? "Processando..." : "Confirmar e Faturar"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ════════════════════════════════════════════════════════════════════
          MODAL — Solicitar Reposição
      ════════════════════════════════════════════════════════════════════ */}
      <Dialog open={modalReposicaoOpen} onOpenChange={(o) => { if (!o) { setModalReposicaoOpen(false); setReposicaoPedidoId(null); setReposicaoErro(null); } }}>
        <DialogContent className="!w-[80vw] !max-w-[80vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Solicitar Reposição — Pedido {pedidoReposicaoDetalhes ? `#${pedidoReposicaoDetalhes.id}` : ""}
            </DialogTitle>
          </DialogHeader>

          {loadingReposicaoDetalhes && <p className="text-center text-muted-foreground py-8">Carregando itens...</p>}

          {pedidoReposicaoDetalhes && !loadingReposicaoDetalhes && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Selecione os itens que precisam de reposição, informe o endereço de origem (palete onde o produto está) e o endereço de destino (picking).
              </p>

              <div className="border rounded overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-2 text-left w-8"></th>
                      <th className="p-2 text-left">Código</th>
                      <th className="p-2 text-left">Descrição</th>
                      <th className="p-2 text-left">Qtd.</th>
                      <th className="p-2 text-left">Origem (Palete)</th>
                      <th className="p-2 text-left">Destino (Picking)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(pedidoReposicaoDetalhes.itens || []).map((item: any) => (
                      <tr key={item.id} className={itensSelecionadosReposicao.has(item.id) ? "bg-blue-50" : "hover:bg-gray-50"}>
                        <td className="p-2">
                          <Checkbox
                            checked={itensSelecionadosReposicao.has(item.id)}
                            onCheckedChange={(checked) => {
                              const next = new Set(itensSelecionadosReposicao);
                              if (checked) next.add(item.id); else next.delete(item.id);
                              setItensSelecionadosReposicao(next);
                            }}
                          />
                        </td>
                        <td className="p-2 font-mono text-xs">{item.productCod || "-"}</td>
                        <td className="p-2">{item.productDescription || "-"}</td>
                        <td className="p-2 text-center">
                          {itensSelecionadosReposicao.has(item.id) ? (
                            <input
                              type="number"
                              min="1"
                              step="1"
                              className="border rounded px-2 py-1 text-xs w-20 text-center"
                              value={quantidadePorItem[item.id] ?? parseFloat(item.quantidade || '0').toFixed(0)}
                              onChange={(e) => setQuantidadePorItem(prev => ({ ...prev, [item.id]: e.target.value }))}
                            />
                          ) : (
                            <span>{parseFloat(item.quantidade || '0').toFixed(0)}</span>
                          )}
                        </td>
                        <td className="p-2">
                          {itensSelecionadosReposicao.has(item.id) && (
                            <div className="relative">
                              <input
                                type="text"
                                className="border rounded px-2 py-1 text-xs w-full"
                                placeholder="Buscar endereço..."
                                value={buscaOrigemPorItem[item.id] ?? enderecoOrigemPorItem[item.id] ?? ""}
                                onChange={(e) => {
                                  setBuscaOrigemPorItem(prev => ({ ...prev, [item.id]: e.target.value }));
                                  setEnderecoOrigemPorItem(prev => ({ ...prev, [item.id]: "" }));
                                }}
                              />
                              {(buscaOrigemPorItem[item.id] || "").length > 0 && !enderecoOrigemPorItem[item.id] && (
                                <div className="absolute z-50 bg-white border rounded shadow-lg w-full max-h-40 overflow-y-auto">
                                  {(item.enderecos || [])
                                    .filter((end: string) => end && end !== 'EXPEDICAO' && end !== 'EXTERNO' && end.toLowerCase().includes((buscaOrigemPorItem[item.id] || "").toLowerCase()))
                                    .map((end: string) => (
                                      <div
                                        key={end}
                                        className="px-2 py-1 text-xs cursor-pointer hover:bg-blue-50"
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          setEnderecoOrigemPorItem(prev => ({ ...prev, [item.id]: end }));
                                          setBuscaOrigemPorItem(prev => ({ ...prev, [item.id]: end }));
                                        }}
                                      >
                                        {end}
                                      </div>
                                    ))}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="p-2">
                          {itensSelecionadosReposicao.has(item.id) && (
                            <div className="relative">
                              <input
                                type="text"
                                className="border rounded px-2 py-1 text-xs w-full"
                                placeholder="Buscar endereço..."
                                value={buscaDestinoPorItem[item.id] ?? enderecoDestinoPorItem[item.id] ?? ""}
                                onChange={(e) => {
                                  setBuscaDestinoPorItem(prev => ({ ...prev, [item.id]: e.target.value }));
                                  setEnderecoDestinoPorItem(prev => ({ ...prev, [item.id]: "" }));
                                }}
                              />
                              {(buscaDestinoPorItem[item.id] || "").length > 0 && !enderecoDestinoPorItem[item.id] && (
                                <div className="absolute z-50 bg-white border rounded shadow-lg w-full max-h-40 overflow-y-auto">
                                  {(enderecosCadastrados as any[])
                                    .filter((end: any) => end.endereco.toLowerCase().includes((buscaDestinoPorItem[item.id] || "").toLowerCase()))
                                    .map((end: any) => (
                                      <div
                                        key={end.id}
                                        className="px-2 py-1 text-xs cursor-pointer hover:bg-blue-50"
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          setEnderecoDestinoPorItem(prev => ({ ...prev, [item.id]: end.endereco }));
                                          setBuscaDestinoPorItem(prev => ({ ...prev, [item.id]: end.endereco }));
                                        }}
                                      >
                                        {end.endereco}
                                      </div>
                                    ))}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {reposicaoErro && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <p>{reposicaoErro}</p>
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                Cada item selecionado gerará uma movimentação do tipo <strong>REPOSIÇÃO</strong> na fila de movimentações.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setModalReposicaoOpen(false); setReposicaoPedidoId(null); setReposicaoErro(null); }}>Cancelar</Button>
            <Button
              onClick={handleConfirmarReposicao}
              disabled={itensSelecionadosReposicao.size === 0 || mutSolicitarReposicao.isPending || loadingReposicaoDetalhes}
            >
              {mutSolicitarReposicao.isPending ? "Solicitando..." : `Solicitar Reposição (${itensSelecionadosReposicao.size} item${itensSelecionadosReposicao.size !== 1 ? "s" : ""})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ════════════════════════════════════════════════════════════════════
          MODAL — Consultar Reposições em Andamento
      ════════════════════════════════════════════════════════════════════ */}
      <Dialog open={modalConsultaReposicaoOpen} onOpenChange={(o) => { if (!o) { setModalConsultaReposicaoOpen(false); setConsultaReposicaoPedidoId(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="h-4 w-4 text-orange-500" />
              Reposições em Andamento
            </DialogTitle>
          </DialogHeader>

          {loadingReposicoes && <p className="text-center text-muted-foreground py-8">Carregando...</p>}

          {!loadingReposicoes && (reposicoesPedido as any[]).length === 0 && (
            <p className="text-center text-muted-foreground py-8">Nenhuma reposição encontrada para este pedido.</p>
          )}

          {!loadingReposicoes && (reposicoesPedido as any[]).length > 0 && (
            <div className="border rounded overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-2 text-left">Produto</th>
                    <th className="p-2 text-left">Qtd.</th>
                    <th className="p-2 text-left">Origem</th>
                    <th className="p-2 text-left">Destino</th>
                    <th className="p-2 text-left">Status</th>
                    <th className="p-2 text-left">Solicitado por</th>
                    <th className="p-2 text-left">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {(reposicoesPedido as any[]).map((rep: any) => (
                    <tr key={rep.id} className="border-t hover:bg-gray-50">
                      <td className="p-2 font-mono text-xs">{rep.productCod}</td>
                      <td className="p-2 text-center">{parseFloat(rep.quantidade).toFixed(0)}</td>
                      <td className="p-2">{rep.enderecoOrigem}</td>
                      <td className="p-2">{rep.enderecoDestino}</td>
                      <td className="p-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          rep.movementStatus === 'CONCLUIDO' ? 'bg-green-100 text-green-800' :
                          rep.movementStatus === 'CANCELADO' ? 'bg-red-100 text-red-800' :
                          rep.movementStatus === 'EM_EXECUCAO' ? 'bg-blue-100 text-blue-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {rep.movementStatus === 'CONCLUIDO' ? 'Concluído' :
                           rep.movementStatus === 'CANCELADO' ? 'Cancelado' :
                           rep.movementStatus === 'EM_EXECUCAO' ? 'Em Execução' : 'Pendente'}
                        </span>
                      </td>
                      <td className="p-2 text-xs text-muted-foreground">{rep.criadoPorNome || "-"}</td>
                      <td className="p-2 text-xs text-muted-foreground">{rep.criadoEm ? new Date(rep.criadoEm).toLocaleDateString("pt-BR") : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setModalConsultaReposicaoOpen(false); setConsultaReposicaoPedidoId(null); }}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
